import React, { useEffect, useState, useRef, createContext, useContext } from 'react';
import { User, Patient, Encounter, LoginSession, AuditEvent, MaternityFile, WalkInClient, BillingItem, Invoice, Quotation, Payment, SalesReceipt, CreditNote, BillingAuditEvent, Medicine, MedicineBatch, Supplier, PurchaseOrder, StockMovement, StockMovementInput, StockReceiptInput, MedicineInput, PharmacySettings, BillingSettings } from '../types';
import { db } from './firebase';
import { collection, doc, setDoc, updateDoc, deleteDoc, onSnapshot, writeBatch, query, where, getDocs, runTransaction } from 'firebase/firestore';
interface DataContextType {
  currentUser: User | null;
  users: User[];
  patients: Patient[];
  encounters: Encounter[];
  loginSessions: LoginSession[];
  auditEvents: AuditEvent[];
  maternityFiles: MaternityFile[];
  walkInClients: WalkInClient[];
  billingItems: BillingItem[];
  invoices: Invoice[];
  quotations: Quotation[];
  payments: Payment[];
  salesReceipts: SalesReceipt[];
  creditNotes: CreditNote[];
  billingAuditEvents: BillingAuditEvent[];
  billingSettings: BillingSettings;
  medicines: Medicine[];
  medicineBatches: MedicineBatch[];
  suppliers: Supplier[];
  purchaseOrders: PurchaseOrder[];
  stockMovements: StockMovement[];
  pharmacySettings: PharmacySettings;
  updateBillingSettings: (settings: Pick<BillingSettings, 'currency' | 'invoicePrefix' | 'receiptPrefix' | 'quotationPrefix' | 'creditNotePrefix' | 'paymentTerms' | 'taxTemplates'>) => Promise<{
    ok: boolean;
    error?: string;
  }>;
  updatePharmacySettings: (settings: Pick<PharmacySettings, 'allowMedicineReturns'>) => Promise<{
    ok: boolean;
    error?: string;
  }>;
  addMedicine: (medicine: MedicineInput) => void;
  updateMedicine: (id: string, medicine: Partial<MedicineInput>) => void;
  addSupplier: (supplier: Omit<Supplier, 'id' | 'createdAt'>) => void;
  updateSupplier: (id: string, supplier: Partial<Supplier>) => void;
  createPurchaseOrder: (purchaseOrder: Omit<PurchaseOrder, 'id' | 'orderNumber' | 'createdAt'>) => void;
  updatePurchaseOrder: (id: string, purchaseOrder: Partial<PurchaseOrder>) => void;
  cancelPurchaseOrder: (id: string, reason: string) => Promise<{
    ok: boolean;
    error?: string;
  }>;
  receiveStock: (receipt: StockReceiptInput) => Promise<{
    ok: boolean;
    error?: string;
  }>;
  recordStockMovement: (movement: StockMovementInput) => Promise<{
    ok: boolean;
    error?: string;
  }>;
  dispenseMedicine: (input: {
    drugName: string;
    encounterId: string;
    patientId: string;
    prescriptionId: string;
  }) => Promise<{
    ok: boolean;
    error?: string;
  }>;
  reverseDispense: (movementId: string, comment: string) => Promise<{
    ok: boolean;
    error?: string;
  }>;
  login: (fullName: string, password: string) => {
    ok: boolean;
    error?: string;
  };
  logout: () => void;
  addUser: (user: Omit<User, 'id'>) => void;
  updateUser: (id: string, user: Partial<User>) => void;
  deleteUser: (id: string) => void;
  addPatient: (patient: Omit<Patient, 'id'>) => void;
  updatePatient: (id: string, patient: Partial<Patient>) => void;
  deletePatient: (id: string) => void;
  createEncounter: (patientId: string) => Encounter;
  updateEncounter: (id: string, encounter: Partial<Encounter>) => void;
  getPatientEncounters: (patientId: string) => Encounter[];
  getActiveEncounters: () => Encounter[];
  deleteLoginSessions: (sessionIds?: string[]) => void;
  deleteLoginSessionsBefore: (date: string) => void;
  deleteLoginSessionsInRange: (start: string, end: string) => void;
  logAudit: (event: Omit<AuditEvent, 'id' | 'at'>) => void;
  addMaternityFile: (file: Omit<MaternityFile, 'id'>) => void;
  updateMaternityFile: (id: string, file: Partial<MaternityFile>) => void;
  deleteMaternityFile: (id: string) => void;
  addWalkInClient: (client: Omit<WalkInClient, 'id'>) => WalkInClient;
  updateWalkInClient: (id: string, client: Partial<WalkInClient>) => void;
  addBillingItem: (item: Omit<BillingItem, 'id'>) => void;
  updateBillingItem: (id: string, item: Partial<BillingItem>) => void;
  deleteBillingItem: (id: string) => void;
  addInvoice: (invoice: Omit<Invoice, 'id'>) => void;
  updateInvoice: (id: string, invoice: Partial<Invoice>) => void;
  deleteInvoice: (id: string) => Promise<{
    ok: boolean;
    error?: string;
  }>;
  addQuotation: (quotation: Omit<Quotation, 'id'>) => void;
  updateQuotation: (id: string, quotation: Partial<Quotation>) => void;
  addPayment: (payment: Omit<Payment, 'id'>) => void;
  getNextSalesReceiptNumber: () => string;
  createSalesReceipt: (receipt: Omit<SalesReceipt, 'id' | 'salesReceiptNumber' | 'createdAt' | 'createdBy'>) => Promise<SalesReceipt | null>;
  deleteSalesReceipt: (id: string) => Promise<{
    ok: boolean;
    error?: string;
  }>;
  addCreditNote: (creditNote: Omit<CreditNote, 'id'>) => void;
  updateCreditNote: (id: string, creditNote: Partial<CreditNote>) => void;
  logBillingAudit: (action: string, description: string) => void;
}
const DataContext = createContext<DataContextType | undefined>(undefined);
const STORAGE_KEY = 'hms_data_v1';
interface StorageData {
  users: User[];
  patients: Patient[];
  encounters: Encounter[];
}
const defaultAdmin: User = {
  id: 'admin-1',
  fullName: 'System Admin',
  password: 'AdminPassword123!',
  role: 'Administrator'
};
// Firestore collection names
const USERS_COL = 'users';
const PATIENTS_COL = 'patients';
const ENCOUNTERS_COL = 'encounters';
const SESSIONS_COL = 'loginSessions';
const AUDIT_COL = 'auditEvents';
const MATERNITY_COL = 'maternityFiles';
const WALKIN_COL = 'walkInClients';
const BILLING_ITEMS_COL = 'billingItems';
const INVOICES_COL = 'invoices';
const QUOTATIONS_COL = 'quotations';
const PAYMENTS_COL = 'payments';
const SALES_RECEIPTS_COL = 'salesReceipts';
const CREDIT_NOTES_COL = 'creditNotes';
const BILLING_AUDIT_COL = 'billingAuditEvents';
const BILLING_SETTINGS_COL = 'billingSettings';
const BILLING_SETTINGS_ID = 'operational';
const MEDICINES_COL = 'medicines';
const MEDICINE_BATCHES_COL = 'medicineBatches';
const SUPPLIERS_COL = 'suppliers';
const PURCHASE_ORDERS_COL = 'purchaseOrders';
const STOCK_MOVEMENTS_COL = 'stockMovements';
const PHARMACY_SETTINGS_COL = 'pharmacySettings';
const PHARMACY_SETTINGS_ID = 'operational';
const SESSION_ID_KEY = 'hms_session_id';
const defaultBillingSettings: BillingSettings = {
  id: BILLING_SETTINGS_ID,
  currency: 'ZMW',
  invoicePrefix: 'INV-',
  receiptPrefix: 'REC-',
  quotationPrefix: 'QUO-',
  creditNotePrefix: 'CN-',
  paymentTerms: 'Due on Receipt',
  taxTemplates: [{
    id: 'vat',
    name: 'VAT',
    type: 'Charge',
    rate: 16,
    isActive: true
  }, {
    id: 'medical-scheme-deduction',
    name: 'Medical Scheme deduction',
    type: 'Deduction',
    rate: 10,
    isActive: true
  }, {
    id: 'tax-exempt',
    name: 'Tax Exempt',
    type: 'Exempt',
    rate: 0,
    isActive: true
  }],
  updatedAt: new Date().toISOString(),
  updatedById: 'System',
  updatedByName: 'System'
};
const defaultPharmacySettings: PharmacySettings = {
  id: PHARMACY_SETTINGS_ID,
  allowMedicineReturns: false,
  updatedAt: new Date().toISOString(),
  updatedById: 'System',
  updatedByName: 'System'
};
type MedicineDocument = Partial<Medicine> & {
  id: string;
};
const MEDICINE_MASTER_FIELDS: Array<keyof Medicine> = ['drugCode', 'medicineId', 'genericName', 'brandName', 'strength', 'dosageForm', 'unit', 'packSize', 'category', 'manufacturer', 'buyingPrice', 'sellingPrice', 'currentQuantity', 'reorderLevel', 'maximumStock', 'status', 'createdAt', 'updatedAt', 'name', 'sku', 'form', 'costPrice'];
function toText(value: unknown, fallback: string) {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}
function toNumber(value: unknown, fallback: number) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
function safeMoneyNumber(value: number) {
  return Number.isFinite(value) ? value : 0;
}
function buildNextSalesReceiptNumber(receipts: SalesReceipt[]) {
  const prefix = 'BMC--';
  const highestSequence = receipts.reduce((highest, receipt) => {
    if (!receipt.salesReceiptNumber.startsWith(prefix)) return highest;
    const sequence = Number.parseInt(receipt.salesReceiptNumber.slice(prefix.length), 10);
    return Number.isFinite(sequence) ? Math.max(highest, sequence) : highest;
  }, 0);
  return `${prefix}${String(highestSequence + 1).padStart(6, '0')}`;
}
function withoutUndefined<T extends object>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, item]) => item !== undefined)) as T;
}
function isPharmacyUser(user: User | null): user is User {
  return user?.role === 'Pharmacy' || user?.role === 'Pharmacy Manager';
}
function isBillingUser(user: User | null): user is User {
  return user?.role === 'Reception' || user?.role === 'Administrator';
}
function isBillingAdministrator(user: User | null): user is User {
  return user?.role === 'Administrator';
}
function medicineCodeSequence(value: unknown) {
  if (typeof value !== 'string') return 0;
  const match = value.trim().match(/^(?:DRG|MED)-(\d+)$/i);
  return match ? Number(match[1]) : 0;
}
function formatDrugCode(sequence: number) {
  return `DRG-${String(sequence).padStart(6, '0')}`;
}
function buildMedicineIdentifier(createdAt: string, documentId: string) {
  const timestamp = new Date(createdAt).getTime();
  const stableTimestamp = Number.isFinite(timestamp) ? timestamp : Date.now();
  const suffix = documentId.replace(/[^a-z0-9]/gi, '').slice(-6) || 'MASTER';
  return `MED-${stableTimestamp}-${suffix.toUpperCase()}`;
}
function normalizeMedicineRecord(source: MedicineDocument, drugCode: string, fallbackNow: string): Medicine {
  const createdAt = toText(source.createdAt, fallbackNow);
  const genericName = toText(source.genericName, toText(source.name, 'Unnamed medicine'));
  const brandName = toText(source.brandName, toText(source.name, genericName));
  const dosageForm = toText(source.dosageForm, toText(source.form, 'Not specified'));
  const reorderLevel = Math.max(0, toNumber(source.reorderLevel, 0));
  const currentQuantity = Math.max(0, toNumber(source.currentQuantity, 0));
  const buyingPrice = Math.max(0, toNumber(source.buyingPrice, toNumber(source.costPrice, 0)));
  return {
    id: source.id,
    drugCode,
    medicineId: toText(source.medicineId, buildMedicineIdentifier(createdAt, source.id)),
    genericName,
    brandName,
    strength: toText(source.strength, 'Not specified'),
    dosageForm,
    unit: toText(source.unit, dosageForm),
    packSize: Math.max(1, toNumber(source.packSize, 1)),
    category: toText(source.category, 'General'),
    manufacturer: toText(source.manufacturer, 'Not specified'),
    buyingPrice,
    sellingPrice: Math.max(0, toNumber(source.sellingPrice, 0)),
    currentQuantity,
    reorderLevel,
    maximumStock: Math.max(currentQuantity, reorderLevel, toNumber(source.maximumStock, Math.max(reorderLevel * 2, 0))),
    status: source.status === 'Inactive' ? 'Inactive' : 'Active',
    createdAt,
    updatedAt: toText(source.updatedAt, createdAt),
    // Existing pharmacy flows continue to consume these compatibility aliases.
    name: toText(source.name, brandName || genericName),
    sku: toText(source.sku, drugCode),
    form: dosageForm,
    costPrice: buyingPrice
  };
}
function normalizeMedicineRecords(records: MedicineDocument[]) {
  const highestKnownSequence = records.reduce((highest, record) => Math.max(highest, medicineCodeSequence(record.drugCode), medicineCodeSequence(record.sku)), 0);
  let nextSequence = highestKnownSequence + 1;
  const usedCodes = new Set<string>();
  const now = new Date().toISOString();
  return records.map(record => {
    const existingSequence = medicineCodeSequence(record.drugCode);
    const existingCode = existingSequence ? formatDrugCode(existingSequence) : '';
    const legacySequence = medicineCodeSequence(record.sku);
    let code: string;
    if (existingSequence && !usedCodes.has(existingCode)) {
      code = existingCode;
    } else if (legacySequence) {
      const legacyCode = formatDrugCode(legacySequence);
      if (!usedCodes.has(legacyCode)) code = legacyCode;else {
        while (usedCodes.has(formatDrugCode(nextSequence))) nextSequence += 1;
        code = formatDrugCode(nextSequence);
        nextSequence += 1;
      }
    } else {
      while (usedCodes.has(formatDrugCode(nextSequence))) nextSequence += 1;
      code = formatDrugCode(nextSequence);
      nextSequence += 1;
    }
    usedCodes.add(code);
    return normalizeMedicineRecord(record, code, now);
  });
}
function medicineBackfill(source: MedicineDocument, normalized: Medicine): Record<string, string | number> {
  return MEDICINE_MASTER_FIELDS.reduce<Record<string, string | number>>((patch, field) => {
    if (source[field] !== normalized[field]) patch[field] = normalized[field];
    return patch;
  }, {});
}
function buildNextDrugCode(existingMedicines: Medicine[]) {
  const highestSequence = existingMedicines.reduce((highest, medicine) => Math.max(highest, medicineCodeSequence(medicine.drugCode), medicineCodeSequence(medicine.sku)), 0);
  const usedCodes = new Set(existingMedicines.map(medicine => medicine.drugCode.toUpperCase()));
  let sequence = highestSequence + 1;
  while (usedCodes.has(formatDrugCode(sequence))) sequence += 1;
  return formatDrugCode(sequence);
}
type MedicineBatchDocument = Partial<MedicineBatch> & {
  id: string;
};
function normalizeMedicineBatchRecord(source: MedicineBatchDocument, fallbackNow: string): MedicineBatch {
  const quantityReceived = Math.max(0, toNumber(source.quantityReceived, toNumber(source.quantity, 0)));
  const quantityRemaining = Math.max(0, toNumber(source.quantityRemaining, toNumber(source.quantity, quantityReceived)));
  const buyingPrice = Math.max(0, toNumber(source.buyingPrice, toNumber(source.costPrice, 0)));
  const dateReceived = toText(source.dateReceived, toText(source.receivedAt, fallbackNow));
  return {
    id: source.id,
    medicineId: toText(source.medicineId, ''),
    batchNumber: toText(source.batchNumber, `LEGACY-${source.id.slice(-6)}`),
    supplierId: typeof source.supplierId === 'string' && source.supplierId.trim() ? source.supplierId.trim() : undefined,
    supplierName: typeof source.supplierName === 'string' && source.supplierName.trim() ? source.supplierName.trim() : undefined,
    quantityReceived,
    quantityRemaining,
    buyingPrice,
    sellingPrice: Math.max(0, toNumber(source.sellingPrice, 0)),
    manufactureDate: toText(source.manufactureDate, ''),
    expiryDate: toText(source.expiryDate, '2099-12-31'),
    invoiceNumber: toText(source.invoiceNumber, ''),
    dateReceived,
    receivedById: toText(source.receivedById, 'System'),
    receivedByName: toText(source.receivedByName, 'System'),
    purchaseOrderId: typeof source.purchaseOrderId === 'string' && source.purchaseOrderId.trim() ? source.purchaseOrderId.trim() : undefined,
    notes: typeof source.notes === 'string' && source.notes.trim() ? source.notes.trim() : undefined,
    // Keep legacy consumers aligned with the canonical batch fields.
    quantity: quantityRemaining,
    receivedAt: dateReceived,
    costPrice: buyingPrice
  };
}
function medicineBatchBackfill(source: MedicineBatchDocument, normalized: MedicineBatch): Record<string, string | number> {
  const fields: Array<keyof MedicineBatch> = ['medicineId', 'batchNumber', 'quantityReceived', 'quantityRemaining', 'buyingPrice', 'sellingPrice', 'manufactureDate', 'expiryDate', 'invoiceNumber', 'dateReceived', 'receivedById', 'receivedByName', 'quantity', 'receivedAt', 'costPrice'];
  return fields.reduce<Record<string, string | number>>((patch, field) => {
    const value = normalized[field];
    if (source[field] !== value && (typeof value === 'string' || typeof value === 'number')) patch[field] = value;
    return patch;
  }, {});
}
// Parse navigator.userAgent into a short, readable device label
const parseDevice = (): {
  device: string;
  userAgent: string;
} => {
  if (typeof navigator === 'undefined') {
    return {
      device: 'Unknown device',
      userAgent: ''
    };
  }
  const ua = navigator.userAgent || '';
  // OS detection
  let os = 'Unknown OS';
  if (/Windows NT 10/i.test(ua)) os = 'Windows 10/11';else if (/Windows NT/i.test(ua)) os = 'Windows';else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macOS';else if (/Android/i.test(ua)) os = 'Android';else if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';else if (/Linux/i.test(ua)) os = 'Linux';else if (/CrOS/i.test(ua)) os = 'ChromeOS';
  // Browser detection (order matters — Edge before Chrome, etc.)
  let browser = 'Unknown browser';
  if (/Edg\//i.test(ua)) browser = 'Edge';else if (/OPR\/|Opera/i.test(ua)) browser = 'Opera';else if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) browser = 'Chrome';else if (/Firefox\//i.test(ua)) browser = 'Firefox';else if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) browser = 'Safari';
  // Device type
  const isMobile = /Mobi|Android.*Mobile|iPhone|iPod/i.test(ua);
  const isTablet = /iPad|Tablet|Android(?!.*Mobile)/i.test(ua);
  const deviceType = isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop';
  return {
    device: `${browser} on ${os} · ${deviceType}`,
    userAgent: ua
  };
};
// Resolve approximate location from IP using a free public service.
// Best-effort: if the call fails, the session simply has no location.
const fetchLocation = async (): Promise<{
  location?: string;
  ipAddress?: string;
}> => {
  try {
    const res = await fetch('https://ipapi.co/json/');
    if (!res.ok) return {};
    const data = await res.json();
    const parts = [data.city, data.region, data.country_name].filter(Boolean);
    return {
      location: parts.length > 0 ? parts.join(', ') : undefined,
      ipAddress: data.ip
    };
  } catch (err) {
    console.warn('Location lookup failed:', err);
    return {};
  }
};
export const DataProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [encounters, setEncounters] = useState<Encounter[]>([]);
  const [loginSessions, setLoginSessions] = useState<LoginSession[]>([]);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [maternityFiles, setMaternityFiles] = useState<MaternityFile[]>([]);
  const [walkInClients, setWalkInClients] = useState<WalkInClient[]>([]);
  const [billingItems, setBillingItems] = useState<BillingItem[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [salesReceipts, setSalesReceipts] = useState<SalesReceipt[]>([]);
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [billingAuditEvents, setBillingAuditEvents] = useState<BillingAuditEvent[]>([]);
  const [billingSettings, setBillingSettings] = useState<BillingSettings>(defaultBillingSettings);
  const [medicines, setMedicines] = useState<Medicine[]>([]);
  const [medicineBatches, setMedicineBatches] = useState<MedicineBatch[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([]);
  const [pharmacySettings, setPharmacySettings] = useState<PharmacySettings>(defaultPharmacySettings);
  const [isLoaded, setIsLoaded] = useState(false);
  // Track whether Firestore has delivered initial data
  const firestoreReady = useRef({
    users: false,
    patients: false,
    encounters: false
  });
  // Holds prescription IDs while a dispense write is being committed, preventing
  // rapid repeat actions from producing duplicate deductions before snapshots refresh.
  const dispensingPrescriptionIdsRef = useRef(new Set<string>());
  // Keeps centrally generated codes unique during rapid consecutive medicine creation.
  const reservedDrugCodesRef = useRef(new Set<string>());
  // One-shot guard so the duplicate-BMC migration runs at most once per session
  const dedupeRanRef = useRef(false);
  // Load from localStorage as initial fallback, then Firestore takes over
  useEffect(() => {
    // Load localStorage cache first for instant display
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed: StorageData = JSON.parse(stored);
        setUsers(parsed.users?.length ? parsed.users : [defaultAdmin]);
        setPatients(parsed.patients || []);
        setEncounters(parsed.encounters || []);
      } catch (e) {
        console.error('Failed to parse stored data', e);
        setUsers([defaultAdmin]);
      }
    } else {
      setUsers([defaultAdmin]);
    }
    // Check session
    const session = sessionStorage.getItem('hms_session');
    if (session) {
      try {
        const user = JSON.parse(session);
        setCurrentUser(user);
      } catch (e) {
        // ignore
      }
    }
    setIsLoaded(true);
    // Set up Firestore real-time listeners
    const unsubUsers = onSnapshot(query(collection(db, USERS_COL)), snapshot => {
      if (!snapshot.empty) {
        const fireUsers = snapshot.docs.map(d => ({
          ...d.data(),
          id: d.id
        }) as User);
        setUsers(fireUsers);
      } else if (!firestoreReady.current.users) {
        // First load and empty — seed default admin to Firestore
        setDoc(doc(db, USERS_COL, defaultAdmin.id), defaultAdmin).catch(console.error);
        setUsers([defaultAdmin]);
      }
      firestoreReady.current.users = true;
    }, error => {
      console.error('Firestore users listener error:', error);
    });
    const unsubPatients = onSnapshot(query(collection(db, PATIENTS_COL)), snapshot => {
      const firePatients = snapshot.docs.map(d => ({
        ...d.data(),
        id: d.id
      }) as Patient);
      setPatients(firePatients);
      firestoreReady.current.patients = true;
    }, error => {
      console.error('Firestore patients listener error:', error);
    });
    const unsubEncounters = onSnapshot(query(collection(db, ENCOUNTERS_COL)), snapshot => {
      const fireEncounters = snapshot.docs.map(d => {
        const data = d.data();
        return {
          ...data,
          id: d.id,
          labRequests: data.labRequests || [],
          radiologyRequests: data.radiologyRequests || [],
          prescriptions: data.prescriptions || []
        } as Encounter;
      });
      setEncounters(fireEncounters);
      firestoreReady.current.encounters = true;
    }, error => {
      console.error('Firestore encounters listener error:', error);
    });
    const unsubSessions = onSnapshot(query(collection(db, SESSIONS_COL)), snapshot => {
      const fireSessions = snapshot.docs.map(d => ({
        ...d.data(),
        id: d.id
      }) as LoginSession);
      setLoginSessions(fireSessions);
    }, error => {
      console.error('Firestore loginSessions listener error:', error);
    });
    const unsubAudit = onSnapshot(collection(db, AUDIT_COL), snapshot => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AuditEvent[];
      setAuditEvents(data);
      localStorage.setItem('bmed_auditEvents', JSON.stringify(data));
    }, error => {
      console.error('Firestore auditEvents listener error:', error);
    });
    const unsubMaternity = onSnapshot(collection(db, MATERNITY_COL), snapshot => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as MaternityFile[];
      setMaternityFiles(data);
      localStorage.setItem('bmed_maternityFiles', JSON.stringify(data));
    }, error => {
      console.error('Firestore maternityFiles listener error:', error);
    });
    const unsubWalkIn = onSnapshot(collection(db, WALKIN_COL), snapshot => {
      setWalkInClients(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as WalkInClient));
    });
    const unsubBillingItems = onSnapshot(collection(db, BILLING_ITEMS_COL), snapshot => {
      if (snapshot.empty && !localStorage.getItem('bmed_billing_items_seeded_v1')) {
        localStorage.setItem('bmed_billing_items_seeded_v1', 'true');
        const seedItems: BillingItem[] = [{
          id: 'seed-item-1',
          itemCode: 'CONS-001',
          serviceName: 'General Consultation',
          category: 'Consultation',
          department: 'General',
          unitPrice: 150,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-2',
          itemCode: 'CONS-002',
          serviceName: 'Specialist Consultation',
          category: 'Consultation',
          department: 'Specialist',
          unitPrice: 300,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-3',
          itemCode: 'CONS-003',
          serviceName: 'Review Consultation',
          category: 'Consultation',
          department: 'General',
          unitPrice: 80,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-4',
          itemCode: 'LAB-FBC',
          serviceName: 'Full Blood Count (FBC)',
          category: 'Laboratory',
          department: 'Laboratory',
          unitPrice: 120,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-5',
          itemCode: 'LAB-UA',
          serviceName: 'Urinalysis',
          category: 'Laboratory',
          department: 'Laboratory',
          unitPrice: 60,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-6',
          itemCode: 'LAB-MRDT',
          serviceName: 'Malaria RDT',
          category: 'Laboratory',
          department: 'Laboratory',
          unitPrice: 50,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-7',
          itemCode: 'LAB-LFT',
          serviceName: 'Liver Function Test',
          category: 'Laboratory',
          department: 'Laboratory',
          unitPrice: 200,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-8',
          itemCode: 'LAB-HIV',
          serviceName: 'HIV Test',
          category: 'Laboratory',
          department: 'Laboratory',
          unitPrice: 90,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-9',
          itemCode: 'LAB-HCG',
          serviceName: 'Beta HCG',
          category: 'Laboratory',
          department: 'Laboratory',
          unitPrice: 180,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-10',
          itemCode: 'RAD-PEL',
          serviceName: 'Pelvic Scan',
          category: 'Radiology',
          department: 'Radiology',
          unitPrice: 350,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-11',
          itemCode: 'RAD-OBS',
          serviceName: 'OBS Scan',
          category: 'Radiology',
          department: 'Radiology',
          unitPrice: 400,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-12',
          itemCode: 'RAD-ABD',
          serviceName: 'Abdominal Scan',
          category: 'Radiology',
          department: 'Radiology',
          unitPrice: 380,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-13',
          itemCode: 'RAD-CXR',
          serviceName: 'CXR (Chest X-Ray)',
          category: 'Radiology',
          department: 'Radiology',
          unitPrice: 250,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-14',
          itemCode: 'PHA-DISP',
          serviceName: 'Dispensing Fee',
          category: 'Pharmacy',
          department: 'Pharmacy',
          unitPrice: 30,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-15',
          itemCode: 'PHA-PARA',
          serviceName: 'Paracetamol',
          category: 'Pharmacy',
          department: 'Pharmacy',
          unitPrice: 25,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-16',
          itemCode: 'PHA-AMOX',
          serviceName: 'Amoxicillin',
          category: 'Pharmacy',
          department: 'Pharmacy',
          unitPrice: 70,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-17',
          itemCode: 'PROC-WD',
          serviceName: 'Wound Dressing',
          category: 'Procedures',
          department: 'Nursing',
          unitPrice: 100,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-18',
          itemCode: 'PROC-SUT',
          serviceName: 'Suturing',
          category: 'Procedures',
          department: 'Nursing',
          unitPrice: 220,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-19',
          itemCode: 'THE-MIN',
          serviceName: 'Minor Theatre Procedure',
          category: 'Theatre',
          department: 'Theatre',
          unitPrice: 1200,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-20',
          itemCode: 'MAT-ND',
          serviceName: 'Normal Delivery',
          category: 'Maternity',
          department: 'Maternity',
          unitPrice: 2500,
          taxRate: 0,
          discountEligibility: true,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-21',
          itemCode: 'REP-MR',
          serviceName: 'Medical Report',
          category: 'Medical Reports',
          department: 'Administration',
          unitPrice: 200,
          taxRate: 16,
          discountEligibility: false,
          status: 'Active',
          createdAt: new Date().toISOString()
        }, {
          id: 'seed-item-22',
          itemCode: 'CORP-SERV',
          serviceName: 'Corporate Services',
          category: 'Corporate Services',
          department: 'Administration',
          unitPrice: 500,
          taxRate: 16,
          discountEligibility: false,
          status: 'Active',
          createdAt: new Date().toISOString()
        }];
        seedItems.forEach(item => {
          setDoc(doc(db, BILLING_ITEMS_COL, item.id), item).catch(console.error);
        });
        setBillingItems(seedItems);
      } else {
        setBillingItems(snapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        }) as BillingItem));
      }
    });
    const unsubInvoices = onSnapshot(collection(db, INVOICES_COL), snapshot => {
      setInvoices(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as Invoice));
    });
    const unsubBillingSettings = onSnapshot(doc(db, BILLING_SETTINGS_COL, BILLING_SETTINGS_ID), snapshot => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setBillingSettings({
          ...defaultBillingSettings,
          ...data,
          id: BILLING_SETTINGS_ID,
          taxTemplates: Array.isArray(data.taxTemplates) && data.taxTemplates.length ? data.taxTemplates : defaultBillingSettings.taxTemplates
        } as BillingSettings);
      } else {
        setDoc(doc(db, BILLING_SETTINGS_COL, BILLING_SETTINGS_ID), defaultBillingSettings).catch(error => console.error('Failed to seed billing settings:', error));
      }
    }, error => console.error('Billing settings listener error:', error));
    const unsubQuotations = onSnapshot(collection(db, QUOTATIONS_COL), snapshot => {
      setQuotations(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as Quotation));
    });
    const unsubPayments = onSnapshot(collection(db, PAYMENTS_COL), snapshot => {
      setPayments(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as Payment));
    });
    const unsubSalesReceipts = onSnapshot(collection(db, SALES_RECEIPTS_COL), snapshot => {
      setSalesReceipts(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as SalesReceipt));
    });
    const unsubCreditNotes = onSnapshot(collection(db, CREDIT_NOTES_COL), snapshot => {
      setCreditNotes(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as CreditNote));
    });
    const unsubBillingAudit = onSnapshot(collection(db, BILLING_AUDIT_COL), snapshot => {
      setBillingAuditEvents(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as BillingAuditEvent));
    });
    const unsubMedicines = onSnapshot(collection(db, MEDICINES_COL), snapshot => {
      if (snapshot.empty && !localStorage.getItem('bmed_pharmacy_inventory_seeded_v1')) {
        localStorage.setItem('bmed_pharmacy_inventory_seeded_v1', 'true');
        const now = new Date().toISOString();
        const seedMedicines = normalizeMedicineRecords([{
          id: 'med-paracetamol',
          name: 'Paracetamol',
          sku: 'MED-001',
          category: 'Analgesic',
          genericName: 'Paracetamol',
          strength: '500 mg',
          form: 'Tablet',
          currentQuantity: 240,
          reorderLevel: 80,
          costPrice: 0.8,
          sellingPrice: 2,
          status: 'Active',
          createdAt: now,
          updatedAt: now
        }, {
          id: 'med-amoxicillin',
          name: 'Amoxicillin',
          sku: 'MED-002',
          category: 'Antibiotic',
          genericName: 'Amoxicillin',
          strength: '500 mg',
          form: 'Capsule',
          currentQuantity: 45,
          reorderLevel: 60,
          costPrice: 3.5,
          sellingPrice: 7,
          status: 'Active',
          createdAt: now,
          updatedAt: now
        }, {
          id: 'med-ibuprofen',
          name: 'Ibuprofen',
          sku: 'MED-003',
          category: 'Anti-inflammatory',
          genericName: 'Ibuprofen',
          strength: '400 mg',
          form: 'Tablet',
          currentQuantity: 0,
          reorderLevel: 40,
          costPrice: 1.4,
          sellingPrice: 3.5,
          status: 'Active',
          createdAt: now,
          updatedAt: now
        }, {
          id: 'med-ors',
          name: 'Oral Rehydration Salts',
          sku: 'MED-004',
          category: 'Gastrointestinal',
          genericName: 'ORS',
          strength: 'WHO formula',
          form: 'Sachet',
          currentQuantity: 120,
          reorderLevel: 30,
          costPrice: 2,
          sellingPrice: 5,
          status: 'Active',
          createdAt: now,
          updatedAt: now
        }, {
          id: 'med-metronidazole',
          name: 'Metronidazole',
          sku: 'MED-005',
          category: 'Antibiotic',
          genericName: 'Metronidazole',
          strength: '400 mg',
          form: 'Tablet',
          currentQuantity: 36,
          reorderLevel: 50,
          costPrice: 1.2,
          sellingPrice: 3,
          status: 'Active',
          createdAt: now,
          updatedAt: now
        }, {
          id: 'med-ferrous',
          name: 'Ferrous Sulphate',
          sku: 'MED-006',
          category: 'Haematinic',
          genericName: 'Ferrous Sulphate',
          strength: '200 mg',
          form: 'Tablet',
          currentQuantity: 150,
          reorderLevel: 50,
          costPrice: 0.7,
          sellingPrice: 2.2,
          status: 'Active',
          createdAt: now,
          updatedAt: now
        }]);
        const seedBatches: MedicineBatch[] = [{
          id: 'batch-para-a',
          medicineId: 'med-paracetamol',
          batchNumber: 'PCT-2401',
          quantityReceived: 240,
          quantityRemaining: 240,
          buyingPrice: 0.8,
          sellingPrice: 2,
          manufactureDate: '2025-08-01',
          expiryDate: '2027-08-31',
          invoiceNumber: 'SEED-PCT-2401',
          dateReceived: now,
          receivedById: 'System',
          receivedByName: 'System',
          quantity: 240,
          receivedAt: now,
          costPrice: 0.8
        }, {
          id: 'batch-amox-a',
          medicineId: 'med-amoxicillin',
          batchNumber: 'AMX-2407',
          quantityReceived: 45,
          quantityRemaining: 45,
          buyingPrice: 3.5,
          sellingPrice: 7,
          manufactureDate: '2025-10-01',
          expiryDate: '2026-10-10',
          invoiceNumber: 'SEED-AMX-2407',
          dateReceived: now,
          receivedById: 'System',
          receivedByName: 'System',
          quantity: 45,
          receivedAt: now,
          costPrice: 3.5
        }, {
          id: 'batch-ors-a',
          medicineId: 'med-ors',
          batchNumber: 'ORS-2410',
          quantityReceived: 120,
          quantityRemaining: 120,
          buyingPrice: 2,
          sellingPrice: 5,
          manufactureDate: '2025-03-01',
          expiryDate: '2027-03-20',
          invoiceNumber: 'SEED-ORS-2410',
          dateReceived: now,
          receivedById: 'System',
          receivedByName: 'System',
          quantity: 120,
          receivedAt: now,
          costPrice: 2
        }, {
          id: 'batch-metro-a',
          medicineId: 'med-metronidazole',
          batchNumber: 'MET-2403',
          quantityReceived: 36,
          quantityRemaining: 36,
          buyingPrice: 1.2,
          sellingPrice: 3,
          manufactureDate: '2025-08-01',
          expiryDate: '2026-08-15',
          invoiceNumber: 'SEED-MET-2403',
          dateReceived: now,
          receivedById: 'System',
          receivedByName: 'System',
          quantity: 36,
          receivedAt: now,
          costPrice: 1.2
        }, {
          id: 'batch-fer-a',
          medicineId: 'med-ferrous',
          batchNumber: 'FER-2406',
          quantityReceived: 150,
          quantityRemaining: 150,
          buyingPrice: 0.7,
          sellingPrice: 2.2,
          manufactureDate: '2025-01-01',
          expiryDate: '2027-01-30',
          invoiceNumber: 'SEED-FER-2406',
          dateReceived: now,
          receivedById: 'System',
          receivedByName: 'System',
          quantity: 150,
          receivedAt: now,
          costPrice: 0.7
        }];
        const seedBatch = writeBatch(db);
        seedMedicines.forEach(medicine => seedBatch.set(doc(db, MEDICINES_COL, medicine.id), medicine));
        seedBatches.forEach(batch => seedBatch.set(doc(db, MEDICINE_BATCHES_COL, batch.id), batch));
        seedBatch.commit().catch(error => console.error('Failed to seed pharmacy inventory:', error));
        setMedicines(seedMedicines);
        setMedicineBatches(seedBatches);
      } else {
        const rawMedicines = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        }) as MedicineDocument);
        const normalizedMedicines = normalizeMedicineRecords(rawMedicines);
        setMedicines(normalizedMedicines);
        rawMedicines.forEach((rawMedicine, index) => {
          const patch = medicineBackfill(rawMedicine, normalizedMedicines[index]);
          if (Object.keys(patch).length > 0) {
            updateDoc(doc(db, MEDICINES_COL, rawMedicine.id), patch).catch(error => console.error('Failed to normalize medicine master data:', error));
          }
        });
      }
    });
    const unsubMedicineBatches = onSnapshot(collection(db, MEDICINE_BATCHES_COL), snapshot => {
      const fallbackNow = new Date().toISOString();
      const rawBatches = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as MedicineBatchDocument);
      const normalizedBatches = rawBatches.map(batch => normalizeMedicineBatchRecord(batch, fallbackNow));
      setMedicineBatches(normalizedBatches);
      rawBatches.forEach((rawBatch, index) => {
        const patch = medicineBatchBackfill(rawBatch, normalizedBatches[index]);
        if (Object.keys(patch).length > 0) {
          updateDoc(doc(db, MEDICINE_BATCHES_COL, rawBatch.id), patch).catch(error => console.error('Failed to normalize medicine batch data:', error));
        }
      });
    });
    const unsubSuppliers = onSnapshot(collection(db, SUPPLIERS_COL), snapshot => {
      setSuppliers(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as Supplier));
    });
    const unsubPurchaseOrders = onSnapshot(collection(db, PURCHASE_ORDERS_COL), snapshot => {
      setPurchaseOrders(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as PurchaseOrder));
    });
    const unsubStockMovements = onSnapshot(collection(db, STOCK_MOVEMENTS_COL), snapshot => {
      setStockMovements(snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as StockMovement));
    });
    const unsubPharmacySettings = onSnapshot(doc(db, PHARMACY_SETTINGS_COL, PHARMACY_SETTINGS_ID), snapshot => {
      if (snapshot.exists()) {
        setPharmacySettings({
          ...defaultPharmacySettings,
          ...snapshot.data(),
          id: PHARMACY_SETTINGS_ID
        } as PharmacySettings);
      } else {
        setDoc(doc(db, PHARMACY_SETTINGS_COL, PHARMACY_SETTINGS_ID), defaultPharmacySettings).catch(error => console.error('Failed to seed pharmacy settings:', error));
      }
    }, error => console.error('Pharmacy settings listener error:', error));
    return () => {
      unsubUsers();
      unsubPatients();
      unsubEncounters();
      unsubSessions();
      unsubAudit();
      unsubMaternity();
      unsubWalkIn();
      unsubBillingItems();
      unsubInvoices();
      unsubBillingSettings();
      unsubQuotations();
      unsubPayments();
      unsubSalesReceipts();
      unsubCreditNotes();
      unsubBillingAudit();
      unsubMedicines();
      unsubMedicineBatches();
      unsubSuppliers();
      unsubPurchaseOrders();
      unsubStockMovements();
      unsubPharmacySettings();
    };
  }, []);
  // Save to localStorage as cache whenever data changes
  useEffect(() => {
    if (!isLoaded) return;
    const data: StorageData = {
      users,
      patients,
      encounters
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [users, patients, encounters, isLoaded]);
  // One-time migration: re-sequence ALL patient file numbers within each
  // registration year so they're contiguous (BMC2025/0001, 0002, 0003 …).
  // Order within a year is by registeredAt ascending (earliest first), with
  // patient.id as a tiebreaker so the result is deterministic across devices.
  // Patient ↔ Encounter ↔ Maternity links use `id`, not `bmcFileNumber`, so
  // renumbering is safe and preserves every relationship.
  //
  // Guarded by a localStorage flag so it runs at most once per browser even
  // across sessions. Bump the flag name (RESEQUENCE_FLAG) to re-trigger.
  const RESEQUENCE_FLAG = 'bmc_resequenced_v1';
  useEffect(() => {
    if (!firestoreReady.current.patients) return;
    if (dedupeRanRef.current) return;
    if (patients.length === 0) return;
    if (localStorage.getItem(RESEQUENCE_FLAG)) {
      dedupeRanRef.current = true;
      return;
    }
    dedupeRanRef.current = true;
    // Group patients by registration year
    const byYear = new Map<number, Patient[]>();
    const currentYear = new Date().getFullYear();
    patients.forEach(p => {
      const year = p.registeredAt ? new Date(p.registeredAt).getFullYear() : currentYear;
      const arr = byYear.get(year) || [];
      arr.push(p);
      byYear.set(year, arr);
    });
    const renumbered: {
      id: string;
      bmcFileNumber: string;
    }[] = [];
    byYear.forEach((group, year) => {
      // Stable ordering: by registeredAt ascending, then by id
      const sorted = [...group].sort((a, b) => {
        const ta = a.registeredAt ? new Date(a.registeredAt).getTime() : 0;
        const tb = b.registeredAt ? new Date(b.registeredAt).getTime() : 0;
        if (ta !== tb) return ta - tb;
        return a.id.localeCompare(b.id);
      });
      const yearPrefix = `BMC${year}/`;
      sorted.forEach((p, idx) => {
        const seq = idx + 1;
        const expected = `${yearPrefix}${seq.toString().padStart(4, '0')}`;
        if (p.bmcFileNumber !== expected) {
          renumbered.push({
            id: p.id,
            bmcFileNumber: expected
          });
        }
      });
    });
    // Mark migration as done regardless of whether anything actually changed,
    // so it doesn't try to run again next session.
    localStorage.setItem(RESEQUENCE_FLAG, new Date().toISOString());
    if (renumbered.length === 0) {
      console.info('[BMC resequence] No changes needed — already in sequence.');
      return;
    }
    console.warn(`[BMC resequence] Updating ${renumbered.length} patient file number(s) to be contiguous per year.`);
    // Apply locally first for instant consistency, then push to Firestore.
    setPatients(prev => prev.map(p => {
      const hit = renumbered.find(r => r.id === p.id);
      return hit ? {
        ...p,
        bmcFileNumber: hit.bmcFileNumber
      } : p;
    }));
    renumbered.forEach(r => {
      updateDoc(doc(db, PATIENTS_COL, r.id), {
        bmcFileNumber: r.bmcFileNumber
      }).catch(err => console.error('Failed to renumber patient', r.id, err));
    });
  }, [patients]);
  const login = (fullName: string, password: string) => {
    const trimmedName = fullName.trim();
    const user = users.find(u => u.fullName.trim().toLowerCase() === trimmedName.toLowerCase());
    if (!user) {
      return {
        ok: false,
        error: 'User not found. Check your full name.'
      };
    }
    if (user.password !== password) {
      return {
        ok: false,
        error: 'Incorrect password.'
      };
    }
    setCurrentUser(user);
    sessionStorage.setItem('hms_session', JSON.stringify(user));
    // Record login session for activity tracking
    const sessionId = `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const {
      device,
      userAgent
    } = parseDevice();
    const session: LoginSession = {
      id: sessionId,
      userId: user.id,
      userName: user.fullName,
      role: user.role,
      loginAt: new Date().toISOString(),
      device,
      userAgent,
      location: 'Resolving…'
    };
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    setLoginSessions(prev => [...prev, session]);
    setDoc(doc(db, SESSIONS_COL, sessionId), session).catch(err => console.error('Failed to record login session:', err));
    // Resolve location asynchronously and patch the session when it arrives
    fetchLocation().then(({
      location,
      ipAddress
    }) => {
      const resolved = location || 'Unknown location';
      setLoginSessions(prev => prev.map(s => s.id === sessionId ? {
        ...s,
        location: resolved,
        ipAddress
      } : s));
      updateDoc(doc(db, SESSIONS_COL, sessionId), {
        location: resolved,
        ipAddress: ipAddress || null
      }).catch(err => console.error('Failed to update session location:', err));
    });
    return {
      ok: true
    };
  };
  const logout = () => {
    const activeSessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (activeSessionId) {
      const logoutAt = new Date().toISOString();
      setLoginSessions(prev => prev.map(s => s.id === activeSessionId ? {
        ...s,
        logoutAt
      } : s));
      updateDoc(doc(db, SESSIONS_COL, activeSessionId), {
        logoutAt
      }).catch(err => console.error('Failed to finalize login session:', err));
      sessionStorage.removeItem(SESSION_ID_KEY);
    }
    setCurrentUser(null);
    sessionStorage.removeItem('hms_session');
  };
  const addUser = (user: Omit<User, 'id'>) => {
    const newUser: User = {
      ...user,
      id: `user-${Date.now()}`
    };
    // Write to Firestore — onSnapshot will update local state
    setDoc(doc(db, USERS_COL, newUser.id), newUser).catch(err => {
      console.error('Failed to add user to Firestore:', err);
      // Fallback: update local state directly
      setUsers(prev => [...prev, newUser]);
    });
  };
  const updateUser = (id: string, data: Partial<User>) => {
    // Optimistic local update
    setUsers(prev => prev.map(u => u.id === id ? {
      ...u,
      ...data
    } : u));
    updateDoc(doc(db, USERS_COL, id), data).catch(err => {
      console.error('Failed to update user in Firestore:', err);
    });
  };
  const deleteUser = (id: string) => {
    setUsers(prev => prev.filter(u => u.id !== id));
    deleteDoc(doc(db, USERS_COL, id)).catch(err => {
      console.error('Failed to delete user from Firestore:', err);
    });
  };
  // Build the next available BMC number for a given year.
  //
  // Strategy:
  //   1. Look at all sequence numbers currently in use for the target year.
  //   2. Walk from 1 upward and pick the FIRST gap (e.g. if 0001, 0002, 0004
  //      are in use, return 0003). This fills holes left by deleted patients
  //      so file numbers stay tight and never collide.
  //   3. If there are no gaps, return max + 1.
  //   4. Final safety: re-check against ALL bmcFileNumbers (any year) and
  //      bump until the candidate is genuinely unique across the database.
  const buildNextBMCNumber = (existingPatients: Patient[], year: number = new Date().getFullYear()): string => {
    const yearPrefix = `BMC${year}/`;
    const usedNumbers = new Set(existingPatients.map(p => p.bmcFileNumber).filter(Boolean));
    const usedSeqsForYear = new Set<number>();
    existingPatients.forEach(p => {
      if (p.bmcFileNumber && p.bmcFileNumber.startsWith(yearPrefix)) {
        const seqStr = p.bmcFileNumber.slice(yearPrefix.length);
        const seq = parseInt(seqStr, 10);
        if (!Number.isNaN(seq)) usedSeqsForYear.add(seq);
      }
    });
    // Find the lowest positive integer not in usedSeqsForYear (gap-filling).
    let nextSeq = 1;
    while (usedSeqsForYear.has(nextSeq)) nextSeq += 1;
    let candidate = `${yearPrefix}${nextSeq.toString().padStart(4, '0')}`;
    // Cross-year safety: ensure global uniqueness against every number on file.
    while (usedNumbers.has(candidate)) {
      nextSeq += 1;
      candidate = `${yearPrefix}${nextSeq.toString().padStart(4, '0')}`;
    }
    return candidate;
  };
  const generateBMCNumber = () => buildNextBMCNumber(patients);
  const addPatient = (patientData: Omit<Patient, 'id'>) => {
    // Compute number against the latest patient list and double-check
    // uniqueness right before save. Bumps if a race created the same number.
    let bmcFileNumber = buildNextBMCNumber(patients);
    const existing = new Set(patients.map(p => p.bmcFileNumber));
    while (existing.has(bmcFileNumber)) {
      // Force the next slot by treating this candidate as already used
      existing.add(bmcFileNumber);
      bmcFileNumber = buildNextBMCNumber([...patients, {
        bmcFileNumber
      } as Patient]);
    }
    const newPatient: Patient = {
      ...patientData,
      id: `pat-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      bmcFileNumber,
      registeredAt: new Date().toISOString()
    };
    // Write to Firestore
    setDoc(doc(db, PATIENTS_COL, newPatient.id), newPatient).catch(err => {
      console.error('Failed to add patient to Firestore:', err);
      setPatients(prev => [...prev, newPatient]);
    });
    // Return immediately for the caller (optimistic)
    setPatients(prev => [...prev, newPatient]);
    return newPatient;
  };
  const updatePatient = (id: string, data: Partial<Patient>) => {
    setPatients(prev => prev.map(p => p.id === id ? {
      ...p,
      ...data
    } : p));
    updateDoc(doc(db, PATIENTS_COL, id), data).catch(err => {
      console.error('Failed to update patient in Firestore:', err);
    });
  };
  const deletePatient = (id: string) => {
    setPatients(prev => prev.filter(p => p.id !== id));
    setEncounters(prev => prev.filter(e => e.patientId !== id));
    // Delete from Firestore
    deleteDoc(doc(db, PATIENTS_COL, id)).catch(err => {
      console.error('Failed to delete patient from Firestore:', err);
    });
    // Also delete associated encounters from Firestore
    encounters.filter(e => e.patientId === id).forEach(e => {
      deleteDoc(doc(db, ENCOUNTERS_COL, e.id)).catch(console.error);
    });
  };
  const createEncounter = (patientId: string) => {
    const newEncounter: Encounter = {
      id: `enc-${Date.now()}`,
      patientId,
      startedAt: new Date().toISOString(),
      stage: 'NURSING',
      pendingLab: false,
      pendingRadiology: false,
      labRequests: [],
      radiologyRequests: [],
      prescriptions: []
    };
    // Optimistic local update
    setEncounters(prev => [...prev, newEncounter]);
    // Write to Firestore
    setDoc(doc(db, ENCOUNTERS_COL, newEncounter.id), newEncounter).catch(err => {
      console.error('Failed to create encounter in Firestore:', err);
    });
    return newEncounter;
  };
  const updateEncounter = (id: string, data: Partial<Encounter>) => {
    // Optimistic local update
    setEncounters(prev => prev.map(e => e.id === id ? {
      ...e,
      ...data
    } : e));
    // Write to Firestore
    updateDoc(doc(db, ENCOUNTERS_COL, id), data as Record<string, any>).catch(err => {
      console.error('Failed to update encounter in Firestore:', err);
    });
  };
  const getPatientEncounters = (patientId: string) => {
    return encounters.filter(e => e.patientId === patientId).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  };
  const getActiveEncounters = () => {
    return encounters.filter(e => e.stage !== 'COMPLETED');
  };
  // ─── Login session deletion ───
  const deleteLoginSessions = (sessionIds?: string[]) => {
    const ids = sessionIds || loginSessions.map(session => session.id);
    setLoginSessions(prev => prev.filter(session => !ids.includes(session.id)));
    ids.forEach(id => deleteDoc(doc(db, SESSIONS_COL, id)).catch(err => console.error('Failed to delete session:', err)));
  };
  const deleteLoginSessionsBefore = (date: string) => {
    const cutoff = new Date(date).getTime();
    const ids = loginSessions.filter(session => new Date(session.loginAt).getTime() < cutoff).map(session => session.id);
    deleteLoginSessions(ids);
  };
  const deleteLoginSessionsInRange = (start: string, end: string) => {
    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();
    const ids = loginSessions.filter(s => {
      const t = new Date(s.loginAt).getTime();
      return t >= startMs && t <= endMs;
    }).map(s => s.id);
    deleteLoginSessions(ids);
  };
  const logAudit = (event: Omit<AuditEvent, 'id' | 'at'>) => {
    const newEvent: AuditEvent = {
      ...event,
      id: `audit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      at: new Date().toISOString()
    };
    setAuditEvents(prev => [...prev, newEvent]);
    setDoc(doc(db, AUDIT_COL, newEvent.id), newEvent).catch(err => console.error('Failed to log audit event:', err));
  };
  const addMaternityFile = (file: Omit<MaternityFile, 'id'>) => {
    const newFile: MaternityFile = {
      ...file,
      id: `mat-${Date.now()}`
    };
    setMaternityFiles(prev => [...prev, newFile]);
    setDoc(doc(db, MATERNITY_COL, newFile.id), newFile).catch(err => {
      console.error('Failed to add maternity file to Firestore:', err);
    });
  };
  const updateMaternityFile = (id: string, data: Partial<MaternityFile>) => {
    setMaternityFiles(prev => prev.map(m => m.id === id ? {
      ...m,
      ...data
    } : m));
    updateDoc(doc(db, MATERNITY_COL, id), data as Record<string, any>).catch(err => {
      console.error('Failed to update maternity file in Firestore:', err);
    });
  };
  const deleteMaternityFile = (id: string) => {
    // Optimistic local removal, then delete from Firestore
    setMaternityFiles(prev => prev.filter(m => m.id !== id));
    deleteDoc(doc(db, MATERNITY_COL, id)).catch(err => {
      console.error('Failed to delete maternity file from Firestore:', err);
    });
  };
  const addWalkInClient = (client: Omit<WalkInClient, 'id'>) => {
    const newClient: WalkInClient = {
      ...client,
      id: `walkin-${Date.now()}`
    };
    setWalkInClients(prev => [...prev, newClient]);
    setDoc(doc(db, WALKIN_COL, newClient.id), newClient).catch(console.error);
    return newClient;
  };
  const updateWalkInClient = (id: string, data: Partial<WalkInClient>) => {
    setWalkInClients(prev => prev.map(c => c.id === id ? {
      ...c,
      ...data
    } : c));
    updateDoc(doc(db, WALKIN_COL, id), data as any).catch(console.error);
  };
  const updateBillingSettings = async (settings: Pick<BillingSettings, 'currency' | 'invoicePrefix' | 'receiptPrefix' | 'quotationPrefix' | 'creditNotePrefix' | 'paymentTerms' | 'taxTemplates'>) => {
    if (!isBillingAdministrator(currentUser)) return {
      ok: false,
      error: 'Only Administrators may manage billing settings and tax templates.'
    };
    const next: BillingSettings = {
      id: BILLING_SETTINGS_ID,
      ...settings,
      updatedAt: new Date().toISOString(),
      updatedById: currentUser.id,
      updatedByName: currentUser.fullName
    };
    try {
      await setDoc(doc(db, BILLING_SETTINGS_COL, BILLING_SETTINGS_ID), next);
      return {
        ok: true
      };
    } catch (error) {
      console.error('Failed to save billing settings:', error);
      return {
        ok: false,
        error: 'Billing settings could not be saved.'
      };
    }
  };
  const addBillingItem = (item: Omit<BillingItem, 'id'>) => {
    if (!isBillingAdministrator(currentUser)) {
      console.error('Only Administrators may manage the service catalogue.');
      return;
    }
    const newItem: BillingItem = {
      ...item,
      id: `item-${Date.now()}`
    };
    setBillingItems(prev => [...prev, newItem]);
    setDoc(doc(db, BILLING_ITEMS_COL, newItem.id), newItem).catch(console.error);
  };
  const updateBillingItem = (id: string, data: Partial<BillingItem>) => {
    if (!isBillingAdministrator(currentUser)) {
      console.error('Only Administrators may manage the service catalogue.');
      return;
    }
    setBillingItems(prev => prev.map(i => i.id === id ? {
      ...i,
      ...data
    } : i));
    updateDoc(doc(db, BILLING_ITEMS_COL, id), data).catch(console.error);
  };
  const deleteBillingItem = (id: string) => {
    if (!isBillingAdministrator(currentUser)) {
      console.error('Only Administrators may manage the service catalogue.');
      return;
    }
    setBillingItems(prev => prev.filter(i => i.id !== id));
    deleteDoc(doc(db, BILLING_ITEMS_COL, id)).catch(console.error);
  };
  const addInvoice = (invoice: Omit<Invoice, 'id'>) => {
    if (!isBillingUser(currentUser)) {
      console.error('Only authorized billing users may create invoices.');
      return;
    }
    const newInvoice: Invoice = {
      ...invoice,
      id: `inv-${Date.now()}`
    };
    setInvoices(prev => [...prev, newInvoice]);
    setDoc(doc(db, INVOICES_COL, newInvoice.id), withoutUndefined(newInvoice)).catch(console.error);
  };
  const updateInvoice = (id: string, data: Partial<Invoice>) => {
    if (!isBillingUser(currentUser)) {
      console.error('Only authorized billing users may update invoices.');
      return;
    }
    setInvoices(prev => prev.map(i => i.id === id ? {
      ...i,
      ...data
    } : i));
    updateDoc(doc(db, INVOICES_COL, id), withoutUndefined(data)).catch(console.error);
  };
  const deleteInvoice = async (id: string) => {
    if (!isBillingAdministrator(currentUser)) return {
      ok: false,
      error: 'Only System Administrators may delete invoices.'
    };
    const invoiceId = id.trim();
    if (!invoiceId) return {
      ok: false,
      error: 'Select a valid invoice to delete.'
    };
    const invoice = invoices.find(item => item.id === invoiceId);
    const invoiceRef = doc(db, INVOICES_COL, invoiceId);
    try {
      // Deleting the invoice is intentionally independent of its status and
      // linked records. Administrators must be able to remove any invoice.
      await deleteDoc(invoiceRef);
      setInvoices(items => items.filter(item => item.id !== invoiceId));
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      return {
        ok: false,
        error: error instanceof Error ? error.message : 'The invoice could not be deleted.'
      };
    }
    void cleanupDeletedInvoiceRecords({
      invoiceId,
      invoiceNumber: invoice?.invoiceNumber || invoiceId,
      user: currentUser,
      onPaymentsRemoved: paymentIds => setPayments(items => items.filter(item => !paymentIds.includes(item.id))),
      onCreditNotesRemoved: creditNoteIds => setCreditNotes(items => items.filter(item => !creditNoteIds.includes(item.id))),
      onAuditAdded: auditEvent => setBillingAuditEvents(items => [...items, auditEvent])
    });
    return {
      ok: true
    };
  };
  const addQuotation = (quotation: Omit<Quotation, 'id'>) => {
    const newQuotation: Quotation = {
      ...quotation,
      id: `quo-${Date.now()}`
    };
    setQuotations(prev => [...prev, newQuotation]);
    setDoc(doc(db, QUOTATIONS_COL, newQuotation.id), newQuotation).catch(console.error);
  };
  const updateQuotation = (id: string, data: Partial<Quotation>) => {
    setQuotations(prev => prev.map(q => q.id === id ? {
      ...q,
      ...data
    } : q));
    updateDoc(doc(db, QUOTATIONS_COL, id), data as any).catch(console.error);
  };
  const addPayment = (payment: Omit<Payment, 'id'>) => {
    if (!isBillingUser(currentUser)) {
      console.error('Only authorized billing users may record payments.');
      return;
    }
    const newPayment: Payment = {
      ...payment,
      id: `pay-${Date.now()}`
    };
    setPayments(prev => [...prev, newPayment]);
    setDoc(doc(db, PAYMENTS_COL, newPayment.id), newPayment).catch(console.error);
  };
  const getNextSalesReceiptNumber = () => buildNextSalesReceiptNumber(salesReceipts);
  const createSalesReceipt = async (receiptData: Omit<SalesReceipt, 'id' | 'salesReceiptNumber' | 'createdAt' | 'createdBy'>) => {
    if (!isBillingUser(currentUser)) {
      console.error('Only authorized billing users may record cash sales.');
      return null;
    }
    const now = new Date().toISOString();
    const items = receiptData.items.map(item => {
      const quantity = Math.max(1, safeMoneyNumber(item.quantity));
      const rate = Math.max(0, safeMoneyNumber(item.rate));
      return {
        ...item,
        itemDescription: item.itemDescription.trim(),
        quantity,
        rate,
        amount: quantity * rate
      };
    });
    const newReceipt: SalesReceipt = {
      ...receiptData,
      id: `sales-receipt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      salesReceiptNumber: buildNextSalesReceiptNumber(salesReceipts),
      billTo: receiptData.billTo.trim(),
      customerPhone: receiptData.customerPhone?.trim() || undefined,
      items,
      totalAmount: items.reduce((total, item) => total + item.amount, 0),
      createdAt: now,
      createdBy: currentUser.id
    };
    setSalesReceipts(previous => [...previous, newReceipt]);
    try {
      await setDoc(doc(db, SALES_RECEIPTS_COL, newReceipt.id), withoutUndefined(newReceipt));
    } catch (error) {
      console.error('Failed to create sales receipt:', error);
      setSalesReceipts(previous => previous.filter(receipt => receipt.id !== newReceipt.id));
      return null;
    }
    logBillingAudit('Create Sales Receipt', `Recorded cash sale ${newReceipt.salesReceiptNumber} for ${newReceipt.totalAmount.toFixed(2)}`);
    return newReceipt;
  };
  const deleteSalesReceipt = async (id: string) => {
    if (!isBillingAdministrator(currentUser)) {
      return {
        ok: false,
        error: 'Only System Administrators may delete sales receipts.'
      };
    }
    const receiptId = id.trim();
    if (!receiptId) {
      return {
        ok: false,
        error: 'Select a valid sales receipt to delete.'
      };
    }
    const receipt = salesReceipts.find(item => item.id === receiptId);
    if (!receipt) {
      return {
        ok: false,
        error: 'The sales receipt could not be found.'
      };
    }
    try {
      await deleteDoc(doc(db, SALES_RECEIPTS_COL, receiptId));
      setSalesReceipts(items => items.filter(item => item.id !== receiptId));
      logBillingAudit('Delete Sales Receipt', `Deleted cash sale ${receipt.salesReceiptNumber} for ${receipt.totalAmount.toFixed(2)}`);
      return {
        ok: true
      };
    } catch (error) {
      console.error('Failed to delete sales receipt:', error);
      return {
        ok: false,
        error: error instanceof Error ? error.message : 'The sales receipt could not be deleted.'
      };
    }
  };
  const addCreditNote = (creditNote: Omit<CreditNote, 'id'>) => {
    const newCreditNote: CreditNote = {
      ...creditNote,
      id: `cn-${Date.now()}`
    };
    setCreditNotes(prev => [...prev, newCreditNote]);
    setDoc(doc(db, CREDIT_NOTES_COL, newCreditNote.id), newCreditNote).catch(console.error);
  };
  const updateCreditNote = (id: string, data: Partial<CreditNote>) => {
    setCreditNotes(prev => prev.map(c => c.id === id ? {
      ...c,
      ...data
    } : c));
    updateDoc(doc(db, CREDIT_NOTES_COL, id), data as any).catch(console.error);
  };
  const addMedicine = (medicineData: MedicineInput) => {
    if (!isPharmacyUser(currentUser)) {
      console.error('Only Pharmacy users may manage pharmacy inventory.');
      return;
    }
    const now = new Date().toISOString();
    const id = `med-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    let drugCode = buildNextDrugCode(medicines);
    while (reservedDrugCodesRef.current.has(drugCode)) {
      const sequence = medicineCodeSequence(drugCode) + 1;
      drugCode = formatDrugCode(sequence);
    }
    reservedDrugCodesRef.current.add(drugCode);
    const medicine = normalizeMedicineRecord({
      ...medicineData,
      id,
      drugCode,
      medicineId: buildMedicineIdentifier(now, id),
      createdAt: now,
      updatedAt: now,
      // Compatibility aliases are maintained centrally, not entered in the UI.
      name: medicineData.brandName.trim() || medicineData.genericName.trim(),
      sku: drugCode,
      form: medicineData.dosageForm,
      costPrice: medicineData.buyingPrice
    }, drugCode, now);
    setMedicines(previous => [...previous, medicine]);
    setDoc(doc(db, MEDICINES_COL, medicine.id), medicine).catch(error => {
      console.error('Failed to add medicine:', error);
      setMedicines(previous => previous.filter(item => item.id !== medicine.id));
    }).finally(() => reservedDrugCodesRef.current.delete(drugCode));
  };
  const updateMedicine = (id: string, data: Partial<MedicineInput>) => {
    if (!isPharmacyUser(currentUser)) {
      console.error('Only Pharmacy users may manage pharmacy inventory.');
      return;
    }
    if (data.buyingPrice !== undefined && currentUser?.role !== 'Pharmacy Manager') {
      console.error('Only Pharmacy Managers may modify buying prices.');
      return;
    }
    const current = medicines.find(medicine => medicine.id === id);
    if (!current) return;
    const now = new Date().toISOString();
    const nextBrandName = data.brandName?.trim() || current.brandName;
    const nextGenericName = data.genericName?.trim() || current.genericName;
    const nextDosageForm = data.dosageForm || current.dosageForm;
    const nextBuyingPrice = data.buyingPrice ?? current.buyingPrice;
    const normalized = normalizeMedicineRecord({
      ...current,
      ...data,
      id,
      updatedAt: now,
      // Keep the established pharmacy aliases synchronized with master edits.
      name: nextBrandName || nextGenericName,
      sku: current.drugCode,
      form: nextDosageForm,
      costPrice: nextBuyingPrice
    }, current.drugCode, current.createdAt);
    const update = medicineBackfill(current, normalized);
    setMedicines(previous => previous.map(medicine => medicine.id === id ? {
      ...medicine,
      ...normalized
    } : medicine));
    updateDoc(doc(db, MEDICINES_COL, id), update).catch(error => console.error('Failed to update medicine:', error));
  };
  const addSupplier = (supplierData: Omit<Supplier, 'id' | 'createdAt'>) => {
    if (!isPharmacyUser(currentUser)) {
      console.error('Only Pharmacy users may manage suppliers.');
      return;
    }
    const supplier: Supplier = {
      ...supplierData,
      id: `supplier-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      createdAt: new Date().toISOString()
    };
    setSuppliers(previous => [...previous, supplier]);
    setDoc(doc(db, SUPPLIERS_COL, supplier.id), supplier).catch(console.error);
  };
  const updateSupplier = (id: string, data: Partial<Supplier>) => {
    if (!isPharmacyUser(currentUser)) {
      console.error('Only Pharmacy users may manage suppliers.');
      return;
    }
    setSuppliers(previous => previous.map(supplier => supplier.id === id ? {
      ...supplier,
      ...data
    } : supplier));
    updateDoc(doc(db, SUPPLIERS_COL, id), data).catch(console.error);
  };
  const createPurchaseOrder = (purchaseOrderData: Omit<PurchaseOrder, 'id' | 'orderNumber' | 'createdAt'>) => {
    if (!isPharmacyUser(currentUser)) {
      console.error('Only Pharmacy users may manage purchase orders.');
      return;
    }
    const changesBuyingPrice = purchaseOrderData.lines.some(line => {
      const medicine = medicines.find(item => item.id === line.medicineId);
      return medicine && line.unitCost !== medicine.buyingPrice;
    });
    if (changesBuyingPrice && currentUser?.role !== 'Pharmacy Manager') {
      console.error('Only Pharmacy Managers may modify buying prices.');
      return;
    }
    const order: PurchaseOrder = {
      ...purchaseOrderData,
      id: `po-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      orderNumber: `PO-${String(purchaseOrders.length + 1).padStart(5, '0')}`,
      createdAt: new Date().toISOString()
    };
    setPurchaseOrders(previous => [...previous, order]);
    setDoc(doc(db, PURCHASE_ORDERS_COL, order.id), withoutUndefined(order)).catch(console.error);
  };
  const updatePurchaseOrder = (id: string, data: Partial<PurchaseOrder>) => {
    if (!isPharmacyUser(currentUser)) {
      console.error('Only Pharmacy users may manage purchase orders.');
      return;
    }
    if (data.lines?.some(line => line.buyingPrice !== undefined || line.unitCost !== undefined) && currentUser?.role !== 'Pharmacy Manager') {
      console.error('Only Pharmacy Managers may modify buying prices.');
      return;
    }
    setPurchaseOrders(previous => previous.map(order => order.id === id ? {
      ...order,
      ...data
    } : order));
    updateDoc(doc(db, PURCHASE_ORDERS_COL, id), withoutUndefined(data)).catch(console.error);
  };
  const updatePharmacySettings = async (settings: Pick<PharmacySettings, 'allowMedicineReturns'>) => {
    if (!isPharmacyUser(currentUser)) return {
      ok: false,
      error: 'Only Pharmacy users may manage pharmacy policy.'
    };
    if (currentUser.role !== 'Pharmacy Manager') return {
      ok: false,
      error: 'Only Pharmacy Managers may update pharmacy policy.'
    };
    const next: PharmacySettings = {
      id: PHARMACY_SETTINGS_ID,
      ...settings,
      updatedAt: new Date().toISOString(),
      updatedById: currentUser.id,
      updatedByName: currentUser.fullName
    };
    try {
      await setDoc(doc(db, PHARMACY_SETTINGS_COL, PHARMACY_SETTINGS_ID), next);
      return {
        ok: true
      };
    } catch (error) {
      console.error('Failed to save pharmacy settings:', error);
      return {
        ok: false,
        error: 'Pharmacy policy could not be saved.'
      };
    }
  };
  const cancelPurchaseOrder = async (id: string, reason: string) => {
    if (!isPharmacyUser(currentUser)) return {
      ok: false,
      error: 'Only Pharmacy users may manage purchase orders.'
    };
    if (currentUser.role !== 'Pharmacy Manager') return {
      ok: false,
      error: 'Only Pharmacy Managers may cancel purchase orders.'
    };
    const order = purchaseOrders.find(item => item.id === id);
    if (!order || order.kind === 'Receipt') return {
      ok: false,
      error: 'Completed delivery receipts cannot be cancelled or deleted.'
    };
    if (!reason.trim()) return {
      ok: false,
      error: 'Enter a cancellation reason.'
    };
    const now = new Date().toISOString();
    const patch = {
      status: 'Cancelled' as const,
      cancelledAt: now,
      cancelledById: currentUser.id,
      cancelledByName: currentUser.fullName,
      cancellationReason: reason.trim()
    };
    try {
      await updateDoc(doc(db, PURCHASE_ORDERS_COL, id), patch);
      return {
        ok: true
      };
    } catch (error) {
      console.error('Failed to cancel purchase order:', error);
      return {
        ok: false,
        error: 'Purchase order could not be cancelled.'
      };
    }
  };
  const recordStockMovement = async (movementInput: StockMovementInput) => {
    if (!isPharmacyUser(currentUser)) return {
      ok: false,
      error: 'Only Pharmacy users may record inventory activity.'
    };
    const isAdjustment = ['Adjustment In', 'Adjustment Out', 'Damaged', 'Transfer', 'Expired Disposal'].includes(movementInput.type);
    const isReturn = ['Return In', 'Return Out'].includes(movementInput.type);
    const isReturnIn = movementInput.type === 'Return In';
    if (isAdjustment && currentUser.role !== 'Pharmacy Manager') return {
      ok: false,
      error: 'Only Pharmacy Managers may adjust inventory.'
    };
    if (isAdjustment && (!movementInput.reason || !movementInput.notes?.trim())) return {
      ok: false,
      error: 'Adjustment reason and comment are required.'
    };
    if (isReturn && !pharmacySettings.allowMedicineReturns) return {
      ok: false,
      error: 'Medicine returns are disabled by hospital policy.'
    };
    if (isReturnIn && !movementInput.batchId) return {
      ok: false,
      error: 'Select the original unexpired batch before returning medicine to stock.'
    };
    if (isReturnIn && movementInput.quantityDelta <= 0 || movementInput.type === 'Return Out' && movementInput.quantityDelta >= 0) return {
      ok: false,
      error: 'Return direction does not match the requested inventory change.'
    };
    if (!Number.isFinite(movementInput.quantityDelta) || movementInput.quantityDelta === 0) return {
      ok: false,
      error: 'Enter a non-zero stock quantity.'
    };
    const now = new Date().toISOString();
    const movementId = `movement-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      await runTransaction(db, async transaction => {
        const medicineRef = doc(db, MEDICINES_COL, movementInput.medicineId);
        const [medicineSnapshot, settingsSnapshot, batchSnapshot] = await Promise.all([transaction.get(medicineRef), transaction.get(doc(db, PHARMACY_SETTINGS_COL, PHARMACY_SETTINGS_ID)), transaction.get(query(collection(db, MEDICINE_BATCHES_COL), where('medicineId', '==', movementInput.medicineId)))]);
        if (!medicineSnapshot.exists()) throw new Error('Medicine was not found.');
        const medicine = {
          id: medicineSnapshot.id,
          ...medicineSnapshot.data()
        } as Medicine;
        const persistedSettings = settingsSnapshot.exists() ? {
          ...defaultPharmacySettings,
          ...settingsSnapshot.data()
        } as PharmacySettings : defaultPharmacySettings;
        if (isReturn && !persistedSettings.allowMedicineReturns) throw new Error('Medicine returns are disabled by hospital policy.');
        const quantityAfter = medicine.currentQuantity + movementInput.quantityDelta;
        if (quantityAfter < 0) throw new Error('Insufficient stock for this transaction.');
        const batches = batchSnapshot.docs.map(snapshot => ({
          id: snapshot.id,
          ...snapshot.data()
        }) as MedicineBatch);
        const selected = movementInput.batchId ? batches.find(batch => batch.id === movementInput.batchId) : undefined;
        if (movementInput.batchId && !selected) throw new Error('Selected batch was not found.');
        if (isReturnIn && (!selected || isBatchExpired(selected, now))) throw new Error('Returned medicines must be added to an existing unexpired batch.');
        const mustUseFefo = movementInput.quantityDelta < 0 && movementInput.type === 'Return Out';
        const eligible = batches.filter(batch => batch.quantityRemaining > 0 && (movementInput.type === 'Expired Disposal' || !isBatchExpired(batch, now))).filter(batch => mustUseFefo || !movementInput.batchId || batch.id === movementInput.batchId).sort((a, b) => a.expiryDate.localeCompare(b.expiryDate));
        const allocations: NonNullable<StockMovement['allocations']> = [];
        let createdBatch: MedicineBatch | undefined;
        if (movementInput.quantityDelta < 0) {
          let remaining = Math.abs(movementInput.quantityDelta);
          eligible.forEach(batch => {
            if (!remaining) return;
            const quantity = Math.min(batch.quantityRemaining, remaining);
            remaining -= quantity;
            allocations.push({
              batchId: batch.id,
              batchNumber: batch.batchNumber,
              quantity: -quantity,
              quantityBefore: batch.quantityRemaining,
              quantityAfter: batch.quantityRemaining - quantity
            });
          });
          if (remaining) throw new Error('No eligible batch has enough stock for this transaction.');
        } else if (selected) {
          allocations.push({
            batchId: selected.id,
            batchNumber: selected.batchNumber,
            quantity: movementInput.quantityDelta,
            quantityBefore: selected.quantityRemaining,
            quantityAfter: selected.quantityRemaining + movementInput.quantityDelta
          });
        } else {
          createdBatch = {
            id: `batch-${movementId}`,
            medicineId: medicine.id,
            batchNumber: `${movementInput.type.replace(/\s/g, '').toUpperCase()}-${Date.now().toString().slice(-6)}`,
            quantityReceived: movementInput.quantityDelta,
            quantityRemaining: movementInput.quantityDelta,
            buyingPrice: medicine.buyingPrice,
            sellingPrice: medicine.sellingPrice,
            manufactureDate: '',
            expiryDate: '2099-12-31',
            invoiceNumber: '',
            dateReceived: now,
            receivedById: currentUser.id,
            receivedByName: currentUser.fullName,
            notes: movementInput.notes?.trim() || undefined,
            quantity: movementInput.quantityDelta,
            receivedAt: now,
            costPrice: medicine.buyingPrice
          };
          allocations.push({
            batchId: createdBatch.id,
            batchNumber: createdBatch.batchNumber,
            quantity: movementInput.quantityDelta,
            quantityBefore: 0,
            quantityAfter: movementInput.quantityDelta
          });
        }
        const primary = allocations[0];
        const movement: StockMovement = {
          id: movementId,
          medicineId: medicine.id,
          medicineName: medicine.name,
          batchId: primary?.batchId,
          batchNumber: primary?.batchNumber,
          allocations,
          type: movementInput.type,
          quantityDelta: movementInput.quantityDelta,
          quantityBefore: medicine.currentQuantity,
          quantityAfter,
          referenceType: movementInput.referenceType || 'Manual',
          referenceId: movementInput.referenceId,
          encounterId: movementInput.encounterId,
          patientId: movementInput.patientId,
          prescriptionId: movementInput.prescriptionId,
          actionPerformed: isAdjustment ? 'Stock Adjustment' : isReturn ? 'Medicine Return' : movementInput.type,
          reason: movementInput.reason,
          authorisedUserId: isAdjustment ? currentUser.id : undefined,
          authorisedUserName: isAdjustment ? currentUser.fullName : undefined,
          notes: movementInput.notes?.trim() || undefined,
          actorId: currentUser.id,
          actorName: currentUser.fullName,
          createdAt: now
        };
        transaction.update(medicineRef, {
          currentQuantity: quantityAfter,
          updatedAt: now
        });
        allocations.forEach(allocation => {
          const batchRef = doc(db, MEDICINE_BATCHES_COL, allocation.batchId);
          if (createdBatch?.id === allocation.batchId) transaction.set(batchRef, withoutUndefined(createdBatch));else transaction.update(batchRef, {
            quantityRemaining: allocation.quantityAfter,
            quantity: allocation.quantityAfter
          });
        });
        transaction.set(doc(db, STOCK_MOVEMENTS_COL, movementId), withoutUndefined(movement));
      });
      return {
        ok: true
      };
    } catch (error) {
      console.error('Failed to record stock movement:', error);
      return {
        ok: false,
        error: error instanceof Error ? error.message : 'The stock movement could not be saved. Inventory was not changed.'
      };
    }
  };
  const receiveStock = async (receipt: StockReceiptInput) => {
    const normalizedReceipt = 'items' in receipt ? receipt : {
      supplierId: receipt.supplierId || '',
      invoiceNumber: receipt.invoiceNumber,
      purchaseOrderId: receipt.purchaseOrderId,
      notes: receipt.notes,
      items: [{
        medicineId: receipt.medicineId,
        batchNumber: receipt.batchNumber,
        quantity: receipt.quantity,
        buyingPrice: receipt.buyingPrice,
        sellingPrice: receipt.sellingPrice,
        manufactureDate: receipt.manufactureDate,
        expiryDate: receipt.expiryDate
      }]
    };
    if (!isPharmacyUser(currentUser)) return {
      ok: false,
      error: 'Only Pharmacy users may receive stock.'
    };
    const supplier = suppliers.find(item => item.id === normalizedReceipt.supplierId);
    if (!supplier) return {
      ok: false,
      error: 'Select the supplier that delivered this stock.'
    };
    if (!normalizedReceipt.invoiceNumber.trim()) return {
      ok: false,
      error: 'Invoice number is required for each delivery.'
    };
    if (!normalizedReceipt.items.length) return {
      ok: false,
      error: 'Add at least one medicine to this receipt.'
    };
    const now = new Date().toISOString();
    const nowDate = new Date();
    for (const [index, item] of normalizedReceipt.items.entries()) {
      const medicine = medicines.find(candidate => candidate.id === item.medicineId);
      const manufactureDate = new Date(`${item.manufactureDate}T00:00:00`);
      const expiryDate = new Date(`${item.expiryDate}T23:59:59`);
      if (!medicine) return {
        ok: false,
        error: `Medicine ${index + 1} was not found.`
      };
      if (!Number.isFinite(item.quantity) || item.quantity <= 0) return {
        ok: false,
        error: `Enter a valid quantity for item ${index + 1}.`
      };
      if (currentUser.role !== 'Pharmacy Manager' && item.buyingPrice !== medicine.buyingPrice) return {
        ok: false,
        error: 'Only Pharmacy Managers may modify buying prices during receiving.'
      };
      if (!Number.isFinite(item.buyingPrice) || item.buyingPrice < 0 || !Number.isFinite(item.sellingPrice) || item.sellingPrice < 0) return {
        ok: false,
        error: `Enter valid prices for item ${index + 1}.`
      };
      if (!item.manufactureDate || Number.isNaN(manufactureDate.getTime()) || manufactureDate > nowDate) return {
        ok: false,
        error: `Enter a valid manufacture date for item ${index + 1}.`
      };
      if (!item.expiryDate || Number.isNaN(expiryDate.getTime()) || expiryDate <= nowDate || expiryDate <= manufactureDate) return {
        ok: false,
        error: `Enter a future expiry date after manufacture for item ${index + 1}.`
      };
    }
    const planningOrder = normalizedReceipt.purchaseOrderId ? purchaseOrders.find(item => item.id === normalizedReceipt.purchaseOrderId) : undefined;
    if (normalizedReceipt.purchaseOrderId && !planningOrder) return {
      ok: false,
      error: 'The selected purchase order was not found.'
    };
    if (planningOrder && (planningOrder.status === 'Received' || planningOrder.status === 'Cancelled')) return {
      ok: false,
      error: 'This purchase order cannot receive additional stock.'
    };
    if (planningOrder && planningOrder.supplierId !== supplier.id) return {
      ok: false,
      error: 'Select the supplier assigned to this purchase order.'
    };
    const receivedByMedicine = new Map<string, number>();
    normalizedReceipt.items.forEach(item => receivedByMedicine.set(item.medicineId, (receivedByMedicine.get(item.medicineId) || 0) + item.quantity));
    if (planningOrder) {
      for (const [medicineId, quantity] of receivedByMedicine) {
        const line = planningOrder.lines.find(candidate => candidate.medicineId === medicineId);
        if (!line || quantity > line.quantityOrdered - line.quantityReceived) return {
          ok: false,
          error: 'A received quantity exceeds the outstanding purchase-order quantity.'
        };
      }
    }
    const receiptOrderId = `po-receipt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const receiptOrder: PurchaseOrder = {
      id: receiptOrderId,
      orderNumber: `PO-RCV-${String(purchaseOrders.filter(item => item.kind === 'Receipt').length + 1).padStart(5, '0')}`,
      supplierId: supplier.id,
      supplierName: supplier.name,
      status: 'Received',
      orderedAt: now,
      dateReceived: now,
      invoiceNumber: normalizedReceipt.invoiceNumber.trim(),
      totalCost: normalizedReceipt.items.reduce((sum, item) => sum + item.quantity * item.buyingPrice, 0),
      receivedById: currentUser.id,
      receivedByName: currentUser.fullName,
      receiptOfPurchaseOrderId: planningOrder?.id,
      kind: 'Receipt',
      notes: normalizedReceipt.notes?.trim() || undefined,
      lines: normalizedReceipt.items.map(item => {
        const medicine = medicines.find(candidate => candidate.id === item.medicineId)!;
        return {
          medicineId: medicine.id,
          medicineName: medicine.name,
          ...(item.batchNumber?.trim() ? {
            batchNumber: item.batchNumber.trim()
          } : {}),
          quantityOrdered: item.quantity,
          quantityReceived: item.quantity,
          unitCost: item.buyingPrice,
          buyingPrice: item.buyingPrice,
          sellingPrice: item.sellingPrice
        };
      }),
      createdBy: currentUser.id,
      createdAt: now
    };
    const medicineUpdates = new Map<string, {
      quantity: number;
      buyingPrice: number;
    }>();
    normalizedReceipt.items.forEach(item => {
      const current = medicineUpdates.get(item.medicineId) || {
        quantity: 0,
        buyingPrice: item.buyingPrice
      };
      medicineUpdates.set(item.medicineId, {
        quantity: current.quantity + item.quantity,
        buyingPrice: item.buyingPrice
      });
    });
    const batchRecords: MedicineBatch[] = normalizedReceipt.items.map((item, index) => {
      const medicine = medicines.find(candidate => candidate.id === item.medicineId)!;
      const generatedBatchNumber = `RCV-${now.slice(2, 10).replace(/-/g, '')}-${String(index + 1).padStart(2, '0')}`;
      return {
        id: `batch-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`,
        medicineId: medicine.id,
        batchNumber: item.batchNumber?.trim() || generatedBatchNumber,
        supplierId: supplier.id,
        supplierName: supplier.name,
        quantityReceived: item.quantity,
        quantityRemaining: item.quantity,
        buyingPrice: item.buyingPrice,
        sellingPrice: item.sellingPrice,
        manufactureDate: item.manufactureDate,
        expiryDate: item.expiryDate,
        invoiceNumber: normalizedReceipt.invoiceNumber.trim(),
        dateReceived: now,
        receivedById: currentUser.id,
        receivedByName: currentUser.fullName,
        purchaseOrderId: receiptOrderId,
        notes: normalizedReceipt.notes?.trim() || undefined,
        quantity: item.quantity,
        receivedAt: now,
        costPrice: item.buyingPrice
      };
    });
    receiptOrder.lines = receiptOrder.lines.map((line, index) => ({
      ...line,
      batchNumber: batchRecords[index].batchNumber
    }));
    const movements: StockMovement[] = batchRecords.map((batchRecord, index) => {
      const medicine = medicines.find(candidate => candidate.id === batchRecord.medicineId)!;
      const previousReceived = batchRecords.slice(0, index).filter(batch => batch.medicineId === medicine.id).reduce((sum, batch) => sum + batch.quantityReceived, 0);
      const quantityBefore = medicine.currentQuantity + previousReceived;
      return {
        id: `movement-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        medicineId: medicine.id,
        medicineName: medicine.name,
        batchId: batchRecord.id,
        batchNumber: batchRecord.batchNumber,
        allocations: [{
          batchId: batchRecord.id,
          batchNumber: batchRecord.batchNumber,
          quantity: batchRecord.quantityReceived,
          quantityBefore: 0,
          quantityAfter: batchRecord.quantityRemaining
        }],
        type: 'Receipt',
        quantityDelta: batchRecord.quantityReceived,
        quantityBefore,
        quantityAfter: quantityBefore + batchRecord.quantityReceived,
        referenceType: 'Purchase Order',
        referenceId: receiptOrderId,
        notes: normalizedReceipt.notes?.trim() || undefined,
        actorId: currentUser.id,
        actorName: currentUser.fullName,
        createdAt: now
      };
    });
    try {
      await runTransaction(db, async transaction => {
        const persistedMedicines = new Map<string, Medicine>();
        for (const medicineId of medicineUpdates.keys()) {
          const snapshot = await transaction.get(doc(db, MEDICINES_COL, medicineId));
          if (!snapshot.exists()) throw new Error('A receipt medicine no longer exists.');
          persistedMedicines.set(medicineId, {
            id: snapshot.id,
            ...snapshot.data()
          } as Medicine);
        }
        let persistedPlanningOrder: PurchaseOrder | undefined;
        if (planningOrder) {
          const snapshot = await transaction.get(doc(db, PURCHASE_ORDERS_COL, planningOrder.id));
          if (!snapshot.exists()) throw new Error('The purchase order no longer exists.');
          persistedPlanningOrder = {
            id: snapshot.id,
            ...snapshot.data()
          } as PurchaseOrder;
          if (persistedPlanningOrder.status === 'Received' || persistedPlanningOrder.status === 'Cancelled') throw new Error('This purchase order cannot receive additional stock.');
          for (const [medicineId, quantity] of receivedByMedicine) {
            const line = persistedPlanningOrder.lines.find(item => item.medicineId === medicineId);
            if (!line || quantity > line.quantityOrdered - line.quantityReceived) throw new Error('A received quantity exceeds the outstanding purchase-order quantity.');
          }
        }
        const runningQuantity = new Map<string, number>();
        const committedMovements = movements.map((movement, index) => {
          const medicine = persistedMedicines.get(movement.medicineId)!;
          const before = runningQuantity.get(medicine.id) ?? medicine.currentQuantity;
          const after = before + batchRecords[index].quantityReceived;
          runningQuantity.set(medicine.id, after);
          return {
            ...movement,
            quantityBefore: before,
            quantityAfter: after,
            actionPerformed: 'Stock Receipt'
          };
        });
        medicineUpdates.forEach((update, medicineId) => {
          const medicine = persistedMedicines.get(medicineId)!;
          transaction.update(doc(db, MEDICINES_COL, medicineId), {
            currentQuantity: medicine.currentQuantity + update.quantity,
            buyingPrice: update.buyingPrice,
            costPrice: update.buyingPrice,
            updatedAt: now
          });
        });
        transaction.set(doc(db, PURCHASE_ORDERS_COL, receiptOrder.id), withoutUndefined(receiptOrder));
        batchRecords.forEach(batchRecord => transaction.set(doc(db, MEDICINE_BATCHES_COL, batchRecord.id), withoutUndefined(batchRecord)));
        committedMovements.forEach(movement => transaction.set(doc(db, STOCK_MOVEMENTS_COL, movement.id), withoutUndefined(movement)));
        if (persistedPlanningOrder) {
          const lines = persistedPlanningOrder.lines.map(line => ({
            ...line,
            quantityReceived: line.quantityReceived + (receivedByMedicine.get(line.medicineId) || 0)
          }));
          transaction.update(doc(db, PURCHASE_ORDERS_COL, persistedPlanningOrder.id), {
            lines,
            status: lines.every(line => line.quantityReceived >= line.quantityOrdered) ? 'Received' : 'Partially Received'
          });
        }
      });
    } catch (error) {
      console.error('Failed to receive stock:', error);
      return {
        ok: false,
        error: error instanceof Error ? error.message : 'Inventory could not be updated. No receipt changes were saved.'
      };
    }
    // Real-time Firestore listeners update local state only after the transaction commits.
    return {
      ok: true
    };
  };
  const dispenseMedicine = async (input: {
    drugName: string;
    encounterId: string;
    patientId: string;
    prescriptionId: string;
  }) => {
    if (!isPharmacyUser(currentUser)) return {
      ok: false,
      error: 'Only Pharmacy users may dispense medicine.'
    };
    const encounter = encounters.find(item => item.id === input.encounterId);
    const prescription = encounter?.prescriptions.find(item => item.id === input.prescriptionId);
    if (!encounter || !prescription) return {
      ok: false,
      error: 'The prescription could not be found.'
    };
    if (dispensingPrescriptionIdsRef.current.has(input.prescriptionId) || prescription.status === 'Dispensed' || stockMovements.some(movement => movement.type === 'Dispense' && movement.prescriptionId === input.prescriptionId && !stockMovements.some(candidate => candidate.reversalOfMovementId === movement.id))) return {
      ok: false,
      error: 'This prescription has already been dispensed.'
    };
    const normalizedDrug = normalizeMedicineName(input.drugName);
    const medicine = medicines.find(item => normalizeMedicineName(item.name) === normalizedDrug || normalizeMedicineName(item.genericName) === normalizedDrug);
    if (!medicine) return {
      ok: false,
      error: 'No matching medicine exists in Pharmacy Inventory. Mark this item Not Available or add stock first.'
    };
    const now = new Date().toISOString();
    const movementId = `movement-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    dispensingPrescriptionIdsRef.current.add(input.prescriptionId);
    try {
      await runTransaction(db, async transaction => {
        const [medicineSnapshot, encounterSnapshot, batchesSnapshot] = await Promise.all([transaction.get(doc(db, MEDICINES_COL, medicine.id)), transaction.get(doc(db, ENCOUNTERS_COL, encounter.id)), transaction.get(query(collection(db, MEDICINE_BATCHES_COL), where('medicineId', '==', medicine.id)))]);
        if (!medicineSnapshot.exists() || !encounterSnapshot.exists()) throw new Error('The dispensing record changed before it could be saved.');
        const persistedMedicine = {
          id: medicineSnapshot.id,
          ...medicineSnapshot.data()
        } as Medicine;
        const persistedEncounter = {
          id: encounterSnapshot.id,
          ...encounterSnapshot.data()
        } as Encounter;
        const persistedPrescription = persistedEncounter.prescriptions.find(item => item.id === input.prescriptionId);
        if (!persistedPrescription || persistedPrescription.status === 'Dispensed') throw new Error('This prescription has already been dispensed.');
        const batch = batchesSnapshot.docs.map(snapshot => ({
          id: snapshot.id,
          ...snapshot.data()
        }) as MedicineBatch).filter(candidate => candidate.quantityRemaining > 0 && !isBatchExpired(candidate, now)).sort((a, b) => a.expiryDate.localeCompare(b.expiryDate))[0];
        if (!batch) throw new Error('No unexpired stock is available for this medicine.');
        if (persistedMedicine.currentQuantity < 1) throw new Error('Insufficient stock for this prescription.');
        const persistedPrescriptions = persistedEncounter.prescriptions.map(item => item.id === input.prescriptionId ? {
          ...item,
          status: 'Dispensed' as const,
          dispensedAt: now,
          dispensedBy: currentUser.id,
          pharmacistName: currentUser.fullName
        } : item);
        const allocation = {
          batchId: batch.id,
          batchNumber: batch.batchNumber,
          quantity: -1,
          quantityBefore: batch.quantityRemaining,
          quantityAfter: batch.quantityRemaining - 1
        };
        const committedMovement: StockMovement = {
          id: movementId,
          medicineId: persistedMedicine.id,
          medicineName: persistedMedicine.name,
          batchId: batch.id,
          batchNumber: batch.batchNumber,
          allocations: [allocation],
          type: 'Dispense',
          quantityDelta: -1,
          quantityBefore: persistedMedicine.currentQuantity,
          quantityAfter: persistedMedicine.currentQuantity - 1,
          referenceType: 'Prescription',
          referenceId: input.prescriptionId,
          encounterId: input.encounterId,
          patientId: input.patientId,
          prescriptionId: input.prescriptionId,
          actionPerformed: 'Prescription Dispensing',
          notes: 'One unit automatically deducted using FEFO from a fresh unexpired batch selection.',
          actorId: currentUser.id,
          actorName: currentUser.fullName,
          createdAt: now
        };
        transaction.update(doc(db, MEDICINES_COL, persistedMedicine.id), {
          currentQuantity: committedMovement.quantityAfter,
          updatedAt: now
        });
        transaction.update(doc(db, MEDICINE_BATCHES_COL, batch.id), {
          quantityRemaining: allocation.quantityAfter,
          quantity: allocation.quantityAfter
        });
        transaction.update(doc(db, ENCOUNTERS_COL, encounter.id), {
          prescriptions: persistedPrescriptions
        });
        transaction.set(doc(db, STOCK_MOVEMENTS_COL, movementId), withoutUndefined(committedMovement));
      });
    } catch (error) {
      console.error('Failed to dispense medicine:', error);
      dispensingPrescriptionIdsRef.current.delete(input.prescriptionId);
      return {
        ok: false,
        error: error instanceof Error ? error.message : 'Dispensing could not be saved. Inventory was not changed.'
      };
    }
    dispensingPrescriptionIdsRef.current.delete(input.prescriptionId);
    return {
      ok: true
    };
  };
  const reverseDispense = async (movementId: string, comment: string) => {
    if (!isPharmacyUser(currentUser)) return {
      ok: false,
      error: 'Only Pharmacy users may access inventory transactions.'
    };
    if (currentUser.role !== 'Pharmacy Manager') return {
      ok: false,
      error: 'Only Pharmacy Managers may reverse dispensing.'
    };
    if (!comment.trim()) return {
      ok: false,
      error: 'A reversal comment is required.'
    };
    const source = stockMovements.find(movement => movement.id === movementId);
    if (!source || source.type !== 'Dispense' || !source.encounterId || !source.prescriptionId) return {
      ok: false,
      error: 'Select a valid dispensing transaction to reverse.'
    };
    if (stockMovements.some(movement => movement.reversalOfMovementId === source.id)) return {
      ok: false,
      error: 'This dispensing transaction has already been reversed.'
    };
    const now = new Date().toISOString();
    const reversalId = `reversal-${source.id}`;
    try {
      await runTransaction(db, async transaction => {
        const sourceSnapshot = await transaction.get(doc(db, STOCK_MOVEMENTS_COL, source.id));
        const medicineSnapshot = await transaction.get(doc(db, MEDICINES_COL, source.medicineId));
        const encounterSnapshot = await transaction.get(doc(db, ENCOUNTERS_COL, source.encounterId!));
        if (!sourceSnapshot.exists() || !medicineSnapshot.exists() || !encounterSnapshot.exists()) throw new Error('The dispensing record is no longer available for reversal.');
        const currentSource = {
          id: sourceSnapshot.id,
          ...sourceSnapshot.data()
        } as StockMovement;
        const medicine = {
          id: medicineSnapshot.id,
          ...medicineSnapshot.data()
        } as Medicine;
        const encounter = {
          id: encounterSnapshot.id,
          ...encounterSnapshot.data()
        } as Encounter;
        if (currentSource.type !== 'Dispense') throw new Error('Only dispensing transactions can be reversed.');
        const prescription = encounter.prescriptions.find(item => item.id === currentSource.prescriptionId);
        if (!prescription || prescription.status !== 'Dispensed') throw new Error('The linked prescription is not in a reversible dispensed state.');
        const existingReversal = await transaction.get(doc(db, STOCK_MOVEMENTS_COL, `reversal-${currentSource.id}`));
        if (existingReversal.exists()) throw new Error('This dispensing transaction has already been reversed.');
        const allocations = currentSource.allocations || (currentSource.batchId && currentSource.batchNumber ? [{
          batchId: currentSource.batchId,
          batchNumber: currentSource.batchNumber,
          quantity: currentSource.quantityDelta,
          quantityBefore: 0,
          quantityAfter: 0
        }] : []);
        if (!allocations.length) throw new Error('No batch provenance is available for this dispense.');
        const reversedAllocations = [] as NonNullable<StockMovement['allocations']>;
        for (const allocation of allocations) {
          const batchSnapshot = await transaction.get(doc(db, MEDICINE_BATCHES_COL, allocation.batchId));
          if (!batchSnapshot.exists()) throw new Error('The original dispensing batch is unavailable.');
          const batch = {
            id: batchSnapshot.id,
            ...batchSnapshot.data()
          } as MedicineBatch;
          const quantity = Math.abs(allocation.quantity);
          reversedAllocations.push({
            batchId: batch.id,
            batchNumber: batch.batchNumber,
            quantity,
            quantityBefore: batch.quantityRemaining,
            quantityAfter: batch.quantityRemaining + quantity
          });
          transaction.update(doc(db, MEDICINE_BATCHES_COL, batch.id), {
            quantityRemaining: batch.quantityRemaining + quantity,
            quantity: batch.quantityRemaining + quantity
          });
        }
        const returnedQuantity = reversedAllocations.reduce((total, allocation) => total + allocation.quantity, 0);
        const reversal: StockMovement = {
          id: reversalId,
          medicineId: medicine.id,
          medicineName: medicine.name,
          batchId: reversedAllocations[0].batchId,
          batchNumber: reversedAllocations[0].batchNumber,
          allocations: reversedAllocations,
          type: 'Return In',
          quantityDelta: returnedQuantity,
          quantityBefore: medicine.currentQuantity,
          quantityAfter: medicine.currentQuantity + returnedQuantity,
          referenceType: 'Prescription',
          referenceId: currentSource.prescriptionId,
          encounterId: currentSource.encounterId,
          patientId: currentSource.patientId,
          prescriptionId: currentSource.prescriptionId,
          reversalOfMovementId: currentSource.id,
          actionPerformed: 'Dispensing Reversal',
          authorisedUserId: currentUser.id,
          authorisedUserName: currentUser.fullName,
          notes: comment.trim(),
          actorId: currentUser.id,
          actorName: currentUser.fullName,
          createdAt: now
        };
        transaction.update(doc(db, MEDICINES_COL, medicine.id), {
          currentQuantity: reversal.quantityAfter,
          updatedAt: now
        });
        transaction.update(doc(db, ENCOUNTERS_COL, encounter.id), {
          prescriptions: encounter.prescriptions.map(item => item.id === currentSource.prescriptionId ? {
            ...item,
            status: 'Pending' as const,
            dispensedAt: undefined,
            dispensedBy: undefined,
            pharmacistName: undefined
          } : item)
        });
        transaction.set(doc(db, STOCK_MOVEMENTS_COL, reversal.id), withoutUndefined(reversal));
      });
      return {
        ok: true
      };
    } catch (error) {
      console.error('Failed to reverse dispense:', error);
      return {
        ok: false,
        error: error instanceof Error ? error.message : 'Dispensing reversal could not be saved.'
      };
    }
  };
  const logBillingAudit = (action: string, description: string) => {
    if (!currentUser) return;
    const newEvent: BillingAuditEvent = {
      id: `baudit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      action,
      description,
      userId: currentUser.id,
      userName: currentUser.fullName,
      createdAt: new Date().toISOString()
    };
    setBillingAuditEvents(prev => [...prev, newEvent]);
    setDoc(doc(db, BILLING_AUDIT_COL, newEvent.id), newEvent).catch(console.error);
  };
  if (!isLoaded) return null;
  return <DataContext.Provider value={{
    currentUser,
    users,
    patients,
    encounters,
    loginSessions,
    auditEvents,
    maternityFiles,
    walkInClients,
    billingItems,
    invoices,
    quotations,
    payments,
    salesReceipts,
    creditNotes,
    billingAuditEvents,
    billingSettings,
    medicines,
    medicineBatches,
    suppliers,
    purchaseOrders,
    stockMovements,
    pharmacySettings,
    updateBillingSettings,
    updatePharmacySettings,
    addMedicine,
    updateMedicine,
    addSupplier,
    updateSupplier,
    createPurchaseOrder,
    updatePurchaseOrder,
    cancelPurchaseOrder,
    receiveStock,
    recordStockMovement,
    dispenseMedicine,
    reverseDispense,
    login,
    logout,
    addUser,
    updateUser,
    deleteUser,
    addPatient,
    updatePatient,
    deletePatient,
    createEncounter,
    updateEncounter,
    getPatientEncounters,
    getActiveEncounters,
    deleteLoginSessions,
    deleteLoginSessionsBefore,
    deleteLoginSessionsInRange,
    logAudit,
    addMaternityFile,
    updateMaternityFile,
    deleteMaternityFile,
    addWalkInClient,
    updateWalkInClient,
    addBillingItem,
    updateBillingItem,
    deleteBillingItem,
    addInvoice,
    updateInvoice,
    deleteInvoice,
    addQuotation,
    updateQuotation,
    addPayment,
    getNextSalesReceiptNumber,
    createSalesReceipt,
    deleteSalesReceipt,
    addCreditNote,
    updateCreditNote,
    logBillingAudit
  }} data-id="element-5626">
      {children}
    </DataContext.Provider>;
};
interface DeletedInvoiceCleanupInput {
  invoiceId: string;
  invoiceNumber: string;
  user: User;
  onPaymentsRemoved: (paymentIds: string[]) => void;
  onCreditNotesRemoved: (creditNoteIds: string[]) => void;
  onAuditAdded: (auditEvent: BillingAuditEvent) => void;
}
async function cleanupDeletedInvoiceRecords({
  invoiceId,
  invoiceNumber,
  user,
  onPaymentsRemoved,
  onCreditNotesRemoved,
  onAuditAdded
}: DeletedInvoiceCleanupInput) {
  try {
    const [paymentsSnapshot, creditNotesSnapshot] = await Promise.all([getDocs(query(collection(db, PAYMENTS_COL), where('invoiceId', '==', invoiceId))), getDocs(query(collection(db, CREDIT_NOTES_COL), where('invoiceId', '==', invoiceId)))]);
    const paymentIds = paymentsSnapshot.docs.map(snapshot => snapshot.id);
    const creditNoteIds = creditNotesSnapshot.docs.map(snapshot => snapshot.id);
    const auditEvent: BillingAuditEvent = {
      id: `baudit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      action: 'Delete Invoice',
      description: `Deleted invoice ${invoiceNumber} with ${paymentIds.length} linked payment(s) and ${creditNoteIds.length} linked credit note(s).`,
      userId: user.id,
      userName: user.fullName,
      createdAt: new Date().toISOString()
    };
    const cleanupBatch = writeBatch(db);
    paymentIds.forEach(paymentId => cleanupBatch.delete(doc(db, PAYMENTS_COL, paymentId)));
    creditNoteIds.forEach(creditNoteId => cleanupBatch.delete(doc(db, CREDIT_NOTES_COL, creditNoteId)));
    cleanupBatch.set(doc(db, BILLING_AUDIT_COL, auditEvent.id), auditEvent);
    await cleanupBatch.commit();
    onPaymentsRemoved(paymentIds);
    onCreditNotesRemoved(creditNoteIds);
    onAuditAdded(auditEvent);
  } catch (error) {
    // Cleanup is deliberately best-effort: no linked record may prevent an
    // administrator from deleting a recorded invoice.
    console.error('Failed to clean up deleted invoice records:', error);
  }
}
function isBatchExpired(batch: MedicineBatch, at: string) {
  return new Date(`${batch.expiryDate}T23:59:59`).getTime() < new Date(at).getTime();
}
function normalizeMedicineName(value: string) {
  return value.toLowerCase().replace(/\([^)]*\)/g, '').replace(/\b\d+(?:\.\d+)?\s*(?:mg|mcg|g|ml|iu|%|mg\/5ml)\b/g, '').replace(/[^a-z0-9]/g, '').trim();
}
export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};